import React, { useState, useEffect, useRef, useCallback, useMemo, useContext } from 'react';
import { createPortal } from 'react-dom';
import { AuthContext } from '../../context/AuthContext';
import { workOrderApi } from '../../api/workOrderApi';
import WorkOrderDetailModal from './WorkOrderDetailModal';

// ---------------------------------------------------------
// [ 스타일 정의 - TimelineTab 원본 100% 복원 ]
// ---------------------------------------------------------
const customStyles = `
  .custom-scrollbar::-webkit-scrollbar { width: 8px; height: 8px; }
  .custom-scrollbar::-webkit-scrollbar-track { background: #f1f3f5; }
  .custom-scrollbar::-webkit-scrollbar-thumb { background: #ced4da; border-radius: 4px; }
  .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #adb5bd; }

  .gantt-tooltip { transition: opacity 0.2s, transform 0.2s; pointer-events: none; opacity: 0; transform: translateY(5px); }
  .task-capsule:hover .gantt-tooltip { opacity: 1; transform: translateY(-50%); }

  .resizer-handle { transition: background-color 0.2s ease; cursor: col-resize; }
  .resizer-handle:hover, .resizer-handle:active { background-color: rgba(13, 110, 253, 0.05) !important; }
  .resizer-handle::before { content: ''; position: absolute; top: 0; bottom: 0; left: 50%; transform: translateX(-50%); width: 1px; background-color: transparent; box-shadow: 1px 0 3px rgba(0,0,0,0.05); }
  .resizer-handle::after { content: '⋮'; position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); height: 32px; width: 14px; display: flex; align-items: center; justify-content: center; background-color: #fff; border: 1px solid #d2d6da; border-radius: 20px; color: #8c98a4; font-size: 14px; font-weight: bolder; box-shadow: 0 2px 5px rgba(0,0,0,0.15), 0 0 1px rgba(0,0,0,0.1); transition: all 0.2s ease; z-index: 10; }
  .resizer-handle:hover::after, .resizer-handle:active::after { border-color: #0d6efd; color: #0d6efd; box-shadow: 0 3px 8px rgba(13,110,253,0.25), 0 0 2px rgba(13,110,253,0.2); }

  /* 탭(Segmented Control) 스타일 고도화 */
  .modern-tabs-container {
    background-color: #f1f3f5;
    padding: 4px;
    border-radius: 12px;
    display: inline-flex;
    gap: 4px;
  }
  .modern-tab {
    padding: 8px 20px;
    font-weight: 600;
    font-size: 14px;
    color: #6c757d;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .modern-tab:hover {
    color: #495057;
    background-color: rgba(255, 255, 255, 0.5);
  }
  .modern-tab.active {
    color: #0d6efd;
    background-color: #ffffff;
    box-shadow: 0 2px 6px rgba(0,0,0,0.05), 0 1px 2px rgba(0,0,0,0.06);
  }

  /* 모던 필터 셀렉트박스 */
  .modern-select {
    appearance: none;
    background-color: #fff;
    border: 1px solid #dee2e6;
    border-radius: 8px;
    padding: 6px 30px 6px 12px;
    font-size: 13px;
    font-weight: 500;
    color: #495057;
    cursor: pointer;
    transition: all 0.2s;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath fill='none' stroke='%23343a40' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M2 5l6 6 6-6'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 10px center;
    background-size: 10px 10px;
  }
  .modern-select:hover { border-color: #adb5bd; }
  .modern-select:focus { border-color: #86b7fe; outline: 0; box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25); }

  /* 뷰모드 스위치 */
  .view-mode-switch {
    background-color: #f1f3f5;
    border-radius: 8px;
    padding: 3px;
    display: inline-flex;
  }
  .view-mode-btn {
    padding: 5px 14px;
    font-size: 12px;
    font-weight: 600;
    color: #6c757d;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s;
    border: none;
    background: transparent;
  }
  .view-mode-btn.active {
    color: #fff;
    background-color: #4c6ef5;
    box-shadow: 0 2px 4px rgba(76, 110, 245, 0.3);
  }
  
  .hide-scrollbar::-webkit-scrollbar { display: none; }
  .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
  .task-row:hover { z-index: 50 !important; }
  .task-name-hover:hover { color: #4c6ef5 !important; }

  @keyframes pulse-glow {
    0%, 100% { transform: translateY(-50%) scale(1); box-shadow: 0 0 0 0 rgba(13, 110, 253, 0.7); }
    50% { transform: translateY(-50%) scale(1.12); box-shadow: 0 0 0 8px rgba(13, 110, 253, 0); }
  }
  .pulse-extend-btn {
    animation: pulse-glow 1.5s infinite ease-in-out;
  }

`;

// ---------------------------------------------------------
// [ 실데이터 파이프라인 연동 ]
// ---------------------------------------------------------

// ---------------------------------------------------------
// [ 인라인 팝업 컴포넌트 복구 ]
// ---------------------------------------------------------
const InlineConfirmPopup = ({ data, onConfirm, onCancel }) => {
  const popupRef = useRef(null);
  const [style, setStyle] = useState({
    position: 'fixed', opacity: 0, top: -9999, left: -9999, zIndex: 99999,
    backgroundColor: '#ffffff', border: '1px solid #e9ecef', borderRadius: '8px',
    padding: '12px 14px', boxShadow: '0 8px 24px rgba(0,0,0,0.1), 0 2px 4px rgba(0,0,0,0.04)',
    minWidth: '150px', maxWidth: '200px', wordBreak: 'keep-all',
  });

  useEffect(() => {
    if (popupRef.current && data) {
      const rect = popupRef.current.getBoundingClientRect();
      const padding = 20;
      let left = data.clientX + 15;
      let top = data.clientY - (rect.height / 2);
      if (left + rect.width > window.innerWidth - padding) left = data.clientX - rect.width - 15;
      if (top + rect.height > window.innerHeight - padding) top = window.innerHeight - rect.height - padding;
      if (left < padding) left = padding;
      if (top < padding) top = padding;
      setStyle(prev => ({ ...prev, left: left + 'px', top: top + 'px', opacity: 1, transition: 'opacity 0.2s' }));
    } else if (!data) {
      setStyle(prev => ({ ...prev, opacity: 0, top: -9999, left: -9999, transition: 'none' }));
    }
  }, [data]);

  if (!data) return null;
  return createPortal(
    <div ref={popupRef} style={style}>
      <div className="d-flex flex-column align-items-center">
        <div className="mb-2 fw-bold text-dark d-flex align-items-center gap-2" style={{ fontSize: '12px', letterSpacing: '-0.3px' }}>
          <i className="fas fa-check-circle text-primary" style={{ fontSize: '13px' }}></i> 변경사항 저장할까요?
        </div>
        <div className="d-flex justify-content-center gap-2 mt-1 w-100">
          <button className="btn btn-light border fw-bolder text-secondary shadow-none" style={{ fontSize: '11px', padding: '3px 14px', borderRadius: '4px' }} onClick={onCancel}>취소</button>
          <button className="btn btn-primary fw-bolder shadow-none" style={{ fontSize: '11px', padding: '3px 14px', borderRadius: '4px' }} onClick={onConfirm}>저장</button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// ---------------------------------------------------------
// [ 다중 선택 커스텀 드롭다운 컴포넌트 ]
// ---------------------------------------------------------
const getAvatarColor = (idx) => {
  const colors = [
    { bg: '#e8f5e9', text: '#28a745' }, { bg: '#fff3cd', text: '#ffc107' },
    { bg: '#e0f7fa', text: '#17a2b8' }, { bg: '#e0e7ff', text: '#4338ca' }, // Indigo (was red)
    { bg: '#e2e3e5', text: '#6c757d' }, { bg: '#cce5ff', text: '#007bff' },
    { bg: '#d4edda', text: '#155724' }, { bg: '#f3e5f5', text: '#8e24aa' },
    { bg: '#fff8e1', text: '#ff8f00' }, { bg: '#ccfbf1', text: '#0f766e' }  // Teal (was deep red)
  ];
  return colors[idx % colors.length];
};

const getStatusBadge = (status) => {
  switch (status) {
    case '대기': return { bg: '#fef3c7', color: '#b45309', icon: 'fas fa-clock', tooltip: '담당자가 아직 접수하지 않은 대기 상태입니다.' };
    case '진행중': return { bg: '#eff6ff', color: '#1d4ed8', icon: 'fas fa-play', tooltip: '담당자가 업무를 접수하여 현재 작업이 진행 중인 상태입니다.' };
    case '검토대기': return { bg: '#f5f3ff', color: '#6d28d9', icon: 'fas fa-paper-plane', tooltip: '처리결과가 제출되어 요청자 검토 대기 중입니다.' };
    case '접수지연': return { bg: '#fef2f2', color: '#b91c1c', icon: 'fas fa-exclamation-circle', tooltip: '담당자가 요청된 기한 내에 접수하지 않아 지연되고 있습니다.' };
    case '진행지연': return { bg: '#fef2f2', color: '#b91c1c', icon: 'fas fa-exclamation-triangle', tooltip: '업무가 시작되었으나 마감 기한보다 지연되고 있습니다.' };
    case '접수·진행지연': return { bg: '#fef2f2', color: '#b91c1c', icon: 'fas fa-times-circle', tooltip: '접수 기한과 마감 기한이 모두 지연된 복합 지연 상태입니다.' };
    case '중단': return { bg: '#fff7ed', color: '#c2410c', icon: 'fas fa-pause', tooltip: '특별한 사유로 인해 업무 진행이 일시적으로 중단된 상태입니다.' };
    case '완료': return { bg: '#f0fdf4', color: '#15803d', icon: 'fas fa-check-circle', tooltip: '모든 검토가 끝나고 업무가 정상적으로 완료되었습니다.' };
    case '취소됨': return { bg: '#fff7ed', color: '#ea580c', icon: 'fas fa-ban', tooltip: '요청자에 의해 해당 업무협조가 취소된 상태입니다.' };
    case '삭제됨': return { bg: '#fef2f2', color: '#dc2626', icon: 'fas fa-trash-alt', tooltip: '삭제 보관함으로 이동된 업무입니다.' };
    default: return { bg: '#f1f5f9', color: '#475569', icon: 'fas fa-tasks', tooltip: '' };
  }
};

const CustomMultiSelect = ({ options, selectedValues, onChange, title, iconClass, type }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggle = (val) => {
    if (val === 'all') {
      onChange([]); // '전체' 클릭 시 빈 배열
    } else {
      if (selectedValues.includes(val)) {
        onChange(selectedValues.filter(v => v !== val));
      } else {
        onChange([...selectedValues, val]);
      }
    }
  };

  const isAllSelected = selectedValues.length === 0;

  return (
    <div className="position-relative" ref={containerRef}>
      <div className="cursor-pointer px-2 py-1 d-flex align-items-center gap-2 fw-bold text-dark hover-bg-light rounded" onClick={() => setIsOpen(!isOpen)} style={{ fontSize: '13px', minWidth: '105px', userSelect: 'none' }}>
        {title} ({isAllSelected ? '전체' : `${selectedValues.length}개 선택`}) <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'} ms-auto text-secondary`} style={{ fontSize: '10px' }}></i>
      </div>
      {isOpen && (
        <div className="position-absolute bg-white border border-secondary border-opacity-25 rounded shadow-sm py-1 mt-1" style={{ top: '100%', left: 0, minWidth: '180px', maxHeight: '300px', overflowY: 'auto', zIndex: 9999 }}>
          <div className="px-3 py-1 cursor-pointer hover-bg-light d-flex align-items-center gap-2" onClick={() => handleToggle('all')}>
            <div className={`d-flex align-items-center justify-content-center border rounded-1 shadow-sm ${isAllSelected ? 'bg-primary border-primary text-white' : 'bg-white border-secondary border-opacity-50 text-white'}`} style={{ width: '15px', height: '15px', fontSize: '9px' }}>
              <i className="fas fa-check"></i>
            </div>
            <div className="d-flex align-items-center gap-2">
              <div className="rounded-circle bg-light d-flex align-items-center justify-content-center text-primary" style={{ width: '20px', height: '20px', fontSize: '11px' }}>
                <i className="fas fa-star"></i>
              </div>
              <span className={`fw-bold ${isAllSelected ? 'text-primary' : 'text-dark'}`} style={{ fontSize: '13px' }}>전체 대상</span>
            </div>
          </div>
          <div className="px-2"><hr className="my-1 border-secondary opacity-10" /></div>
          {options.map(opt => {
            const isSelected = selectedValues.includes(opt.value);

            let colorInfo, renderIcon, tooltipText;
            if (type === 'status') {
              const badge = getStatusBadge(opt.label);
              colorInfo = { bg: badge.bg, text: badge.color };
              renderIcon = <i className={badge.icon} style={{ fontSize: '10px' }}></i>;
              tooltipText = badge.tooltip;
            } else {
              colorInfo = getAvatarColor(opt.idx || 0);
              renderIcon = iconClass ? <i className={iconClass} style={{ fontSize: '10px' }}></i> : opt.label.charAt(0);
              tooltipText = opt.label;
            }

            return (
              <div key={opt.value} className="px-3 py-1 cursor-pointer hover-bg-light d-flex align-items-center gap-2" onClick={() => handleToggle(opt.value)}>
                <div className={`d-flex align-items-center justify-content-center border rounded-1 shadow-sm ${isSelected ? 'bg-primary border-primary text-white' : 'bg-white border-secondary border-opacity-50 text-white'}`} style={{ width: '15px', height: '15px', fontSize: '9px' }}>
                  <i className="fas fa-check"></i>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <div className="rounded-circle d-flex align-items-center justify-content-center fw-bolder shadow-sm" style={{ width: '20px', height: '20px', fontSize: '11px', backgroundColor: colorInfo.bg, color: colorInfo.text, cursor: type === 'status' ? 'help' : 'pointer' }} title={tooltipText}>
                    {renderIcon}
                  </div>
                  <span className={isSelected ? 'text-dark fw-bold' : 'text-secondary'} style={{ fontSize: '13px' }}>{opt.label}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const MIN_DAY_WIDTH = 34; // 팽창 방지용
const DAY_WIDTH = MIN_DAY_WIDTH;
const ROW_HEIGHT = 38;

export default function WorkOrderTimeline() {
  const { user } = useContext(AuthContext);

  // ── 권한 판별 (관리자 3단 권한 분기) ──
  // 1) 최고관리자 (userid === 'admin' && role === 'admin'): 체크박스 미노출, 항상 전사 전체 업무 표출
  // 2) 일반관리자 (userid !== 'admin' && role === 'admin'): 체크박스 노출, 체크 시 전사 전체 업무 / 미체크 시 본인 업무
  // 3) 일반사용자 (role !== 'admin'): 체크박스 미노출, 본인 업무만 표출
  const isSuperAdmin = (user?.userid === 'admin' || user?.id === 'admin') && (user?.role === 'admin' || user?.is_admin === 1 || user?.is_admin === true);
  const isRegularAdmin = !isSuperAdmin && (user?.role === 'admin' || user?.is_admin === 1 || user?.is_admin === true);
  const isAdminRole = isRegularAdmin;

  const [adminAllChecked, setAdminAllChecked] = useState(false);
  const effectiveAdminAll = isSuperAdmin ? true : (isRegularAdmin ? adminAllChecked : false);

  const [filterCategory, setFilterCategory] = useState('received'); // received, sent, cc
  const [viewMode, setViewMode] = useState('byTask'); // byTask(지시명별), byUser(담당자별)

  // 상세 필터 (다중 선택을 위해 배열 상태 사용, 빈 배열은 '전체' 의미)
  const [filterDept, setFilterDept] = useState([]);
  const [filterUser, setFilterUser] = useState([]);
  const [filterStatus, setFilterStatus] = useState([]);

  // 실데이터 상태 관리
  const [rawTasks, setRawTasks] = useState([]);
  const [globalRange, setGlobalRange] = useState({ min: null, max: null });
  const [, setLoading] = useState(false);
  const [detailModalWorkOrderId, setDetailModalWorkOrderId] = useState(null);

  // 모달 오픈 시 타임라인 안내 툴팁 및 팝업 즉시 닫기
  useEffect(() => {
    if (detailModalWorkOrderId) {
      setTooltipData(null);
      setPopupData(null);
    }
  }, [detailModalWorkOrderId]);

  // 동적 부서 및 사원 목록 추출 (실데이터 rawTasks 기반 가나다순 정렬)
  const isReceived = filterCategory === 'received';

  const deptOptions = useMemo(() => {
    if (!Array.isArray(rawTasks)) return [];
    const depts = rawTasks.map(u => isReceived ? u.authorDept : u.department).filter(Boolean);
    return Array.from(new Set(depts)).sort((a, b) => a.localeCompare(b));
  }, [rawTasks, isReceived]);

  const userOptions = useMemo(() => {
    if (!Array.isArray(rawTasks)) return [];
    const userMap = new Map();
    rawTasks.forEach(u => {
      const target = isReceived ? u.author : u.assignee;
      if (target) {
        const label = target.split(' (')[0] || target;
        userMap.set(target, { label, value: target });
      }
    });
    return Array.from(userMap.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [rawTasks, isReceived]);

  const [tasksData, setTasksData] = useState([]);
  const [expandedProjects, setExpandedProjects] = useState({});

  // 커스텀 달력 팝업 상태
  const [showMonthPicker, setShowMonthPicker] = useState(false);
  const [pickerYear, setPickerYear] = useState(new Date().getFullYear());
  const monthPickerContainerRef = useRef(null);
  const [activeMonthsCache, setActiveMonthsCache] = useState({});

  // 타임라인 가이드 팝오버 상태
  const [showGuidePopover, setShowGuidePopover] = useState(false);
  const [guideTab, setGuideTab] = useState('period'); // 'period' | 'schedule' | 'progress'
  const guideContainerRef = useRef(null);

  // 팝업 열릴 때 또는 연도/카테고리/권한 변경 시 연도별 활성 월 초경량 조회 & 캐싱
  useEffect(() => {
    if (!showMonthPicker) return;
    const cacheKey = `${filterCategory}_${pickerYear}_${effectiveAdminAll}`;
    if (activeMonthsCache[cacheKey]) return;

    let isMounted = true;
    workOrderApi.getActiveMonths({ year: pickerYear, category: filterCategory, adminAll: effectiveAdminAll })
      .then(res => {
        if (isMounted && res && res.success && Array.isArray(res.data)) {
          setActiveMonthsCache(prev => ({ ...prev, [cacheKey]: res.data }));
        }
      })
      .catch(err => console.error('[getActiveMonths] error:', err));

    return () => { isMounted = false; };
  }, [showMonthPicker, pickerYear, filterCategory, activeMonthsCache, effectiveAdminAll]);

  const currentActiveMonths = useMemo(() => {
    const cacheKey = `${filterCategory}_${pickerYear}_${effectiveAdminAll}`;
    return activeMonthsCache[cacheKey] || [];
  }, [activeMonthsCache, filterCategory, pickerYear, effectiveAdminAll]);

  // 바깥 영역 클릭 시 달력 팝업 닫기
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showMonthPicker && monthPickerContainerRef.current && !monthPickerContainerRef.current.contains(e.target)) {
        setShowMonthPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMonthPicker]);

  // 바깥 영역 클릭 시 가이드 팝오버 닫기
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showGuidePopover && guideContainerRef.current && !guideContainerRef.current.contains(e.target)) {
        setShowGuidePopover(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showGuidePopover]);

  // 총 진척도 동적 계산
  const totalProgress = useMemo(() => {
    if (tasksData.length === 0) return 0;
    const sum = tasksData.reduce((acc, proj) => acc + proj.progress, 0);
    return Math.round(sum / tasksData.length);
  }, [tasksData]);

  // 필터 및 모드 초기화 함수
  const handleResetFilters = () => {
    setFilterCategory('received');
    setFilterDept([]);
    setFilterUser([]);
    setFilterStatus([]);
    setViewMode('byTask');

    // 캔버스 뷰를 오늘 기준 3개월(전달, 이번달, 다음달)로 완벽 초기화
    const dStart = new Date(); dStart.setDate(1); dStart.setHours(0, 0, 0, 0); dStart.setMonth(dStart.getMonth() - 1);
    setCanvasStart(dStart);
    const dEnd = new Date(); dEnd.setDate(0); dEnd.setHours(23, 59, 59, 999); dEnd.setMonth(dEnd.getMonth() + 2);
    setCanvasEnd(dEnd);
  };

  // 1. 타임라인 스마트 확장 (Lazy Loading) 상태 관리
  const [canvasStart, setCanvasStart] = useState(() => {
    const d = new Date(); d.setDate(1); d.setHours(0, 0, 0, 0); d.setMonth(d.getMonth() - 1); return d;
  });
  const [canvasEnd, setCanvasEnd] = useState(() => {
    const d = new Date(); d.setDate(0); d.setHours(23, 59, 59, 999); d.setMonth(d.getMonth() + 2); return d;
  });

  const projectStart = canvasStart;
  const projectEnd = canvasEnd;

  // 전체 데이터의 실제 최소/최대 날짜 파악
  const { globalMinDate, globalMaxDate } = useMemo(() => {
    if (!tasksData || tasksData.length === 0) return { globalMinDate: null, globalMaxDate: null };
    let min = new Date(tasksData[0].start);
    let max = new Date(tasksData[0].end);
    tasksData.forEach(p => {
      const pStart = new Date(p.start); const pEnd = new Date(p.end);
      if (pStart < min) min = pStart;
      if (pEnd > max) max = pEnd;
    });
    return { globalMinDate: min, globalMaxDate: max };
  }, [tasksData]);

  const hasMorePast = Boolean(
    (globalRange.min && new Date(globalRange.min) < canvasStart) ||
    (globalMinDate && globalMinDate < canvasStart)
  );
  const hasMoreFuture = Boolean(
    (globalRange.max && new Date(globalRange.max) > canvasEnd) ||
    (globalMaxDate && globalMaxDate > canvasEnd)
  );

  const SCROLL_AMOUNT = 400; // 기본 스크롤 넓이

  // 글로벌 달력 연장 함수
  const handleExtendPast = () => {
    setCanvasStart(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1, 0, 0, 0, 0));
    setTimeout(() => {
      if (bodyRightRef.current) bodyRightRef.current.scrollBy({ left: -SCROLL_AMOUNT, behavior: 'smooth' });
    }, 50);
  };

  const handleExtendFuture = () => {
    setCanvasEnd(prev => new Date(prev.getFullYear(), prev.getMonth() + 2, 0, 23, 59, 59, 999));
    setTimeout(() => {
      if (bodyRightRef.current) bodyRightRef.current.scrollBy({ left: SCROLL_AMOUNT, behavior: 'smooth' });
    }, 50);
  };


  // 값들을 useMemo로 묶어 의존성 문제(에러) 완전 해결
  const { canvasWidth, isTodayInRange, todayOffset } = useMemo(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const isToday = today >= projectStart && today <= projectEnd;
    const tDays = Math.floor((projectEnd - projectStart) / (1000 * 60 * 60 * 24)) + 1;
    const offset = isToday ? Math.floor((today - projectStart) / (1000 * 60 * 60 * 24)) * DAY_WIDTH : -1;
    return { canvasWidth: tDays * DAY_WIDTH, isTodayInRange: isToday, todayOffset: offset };
  }, [projectStart, projectEnd]);

  // 실데이터 API 조회 (화면 캔버스 기간 및 카테고리 기반)
  const loadTimelineTasks = useCallback(async () => {
    try {
      setLoading(true);
      const fromStr = canvasStart.toISOString().split('T')[0];
      const toStr = canvasEnd.toISOString().split('T')[0];
      const res = await workOrderApi.getTimelineTasks({
        category: filterCategory,
        from_date: fromStr,
        to_date: toStr,
        adminAll: effectiveAdminAll
      });
      if (res && res.success) {
        const data = res.data;
        if (Array.isArray(data)) {
          setRawTasks(data);
        } else if (data && typeof data === 'object') {
          setRawTasks(Array.isArray(data.tasks) ? data.tasks : []);
          setGlobalRange({ min: data.min_date || null, max: data.max_date || null });
        } else {
          setRawTasks([]);
        }
      }
    } catch (err) {
      console.error('타임라인 데이터 로드 실패:', err);
    } finally {
      setLoading(false);
    }
  }, [filterCategory, canvasStart, canvasEnd, effectiveAdminAll]);

  useEffect(() => {
    loadTimelineTasks();
  }, [loadTimelineTasks]);

  // 2. 동적 데이터 파이프라인 (필터링 -> 그룹핑)
  useEffect(() => {
    if (!Array.isArray(rawTasks)) return;
    const isRec = filterCategory === 'received';
    const filtered = rawTasks.filter(d => {
      const userVal = isRec ? d.author : d.assignee;
      const deptVal = isRec ? d.authorDept : d.department;
      return (
        (filterDept.length === 0 || filterDept.includes(deptVal)) &&
        (filterUser.length === 0 || filterUser.some(u => u === userVal || u.startsWith(userVal))) &&
        (filterStatus.length === 0 || filterStatus.includes(d.status))
      );
    });

    const groups = {};
    filtered.forEach(task => {
      let groupKey, childName;
      if (isRec) {
        groupKey = task.author || '요청자 미지정';
        childName = task.name;
      } else {
        groupKey = viewMode === 'byUser' ? task.assignee : task.taskGroup;
        childName = viewMode === 'byTask' ? task.assignee : task.name;
      }
      if (!groups[groupKey]) groups[groupKey] = { id: groupKey, name: groupKey, type: 'project', tasks: [] };
      groups[groupKey].tasks.push({ ...task, name: childName });
    });

    const projectList = Object.values(groups).map(p => {
      if (p.tasks.length === 0) return p;
      if (isRec || viewMode === 'byUser') {
        p.tasks.sort((a, b) => new Date(b.start) - new Date(a.start) || new Date(b.end) - new Date(a.end));
      } else if (viewMode === 'byTask') {
        p.tasks.sort((a, b) => (a.name || '').localeCompare(b.name || '', 'ko'));
      }
      const starts = p.tasks.map(t => new Date(t.start));
      const ends = p.tasks.map(t => new Date(t.end));
      // 상위 그룹 진척도: 검토대기 중인 태스크는 미승인이므로 상위 합산에서 제외 (0% 반영)
      const avg = Math.round(p.tasks.reduce((sum, t) => {
        if (t.status === '검토대기') return sum;
        return sum + (Number(t.progress) || 0);
      }, 0) / p.tasks.length);
      return {
        ...p,
        start: new Date(Math.min(...starts)).toISOString().split('T')[0],
        end: new Date(Math.max(...ends)).toISOString().split('T')[0],
        progress: avg
      };
    });

    if (isRec || viewMode === 'byUser') {
      projectList.sort((a, b) => (a.name || '').localeCompare(b.name || '', 'ko'));
    } else if (viewMode === 'byTask') {
      projectList.sort((a, b) => new Date(b.start) - new Date(a.start) || new Date(b.end) - new Date(a.end));
    }

    setTasksData(projectList);
    const exp = {};
    projectList.forEach(p => exp[p.id] = true); // 기본 펼침
    setExpandedProjects(exp);
  }, [rawTasks, viewMode, filterDept, filterUser, filterStatus, filterCategory]);

  const toggleProject = (pid) => setExpandedProjects(prev => ({ ...prev, [pid]: !prev[pid] }));
  const expandAll = () => { const exp = {}; tasksData.forEach(p => exp[p.id] = true); setExpandedProjects(exp); };
  const collapseAll = () => { const exp = {}; tasksData.forEach(p => exp[p.id] = false); setExpandedProjects(exp); };

  // 리사이저 관리
  const [listWidth, setListWidth] = useState(420);
  const containerRef = useRef(null);
  const isResizingRef = useRef(false);

  const startResizing = useCallback(() => { isResizingRef.current = true; document.body.style.cursor = 'col-resize'; document.body.style.userSelect = 'none'; }, []);
  const stopResizing = useCallback(() => { if (isResizingRef.current) { isResizingRef.current = false; document.body.style.cursor = ''; document.body.style.userSelect = ''; } }, []);
  const resize = useCallback((e) => {
    if (isResizingRef.current && containerRef.current) {
      let newWidth = e.clientX - containerRef.current.getBoundingClientRect().left;
      if (newWidth < 200) newWidth = 200; if (newWidth > 900) newWidth = 900;
      setListWidth(newWidth);
    }
  }, []);
  useEffect(() => {
    window.addEventListener('mousemove', resize); window.addEventListener('mouseup', stopResizing);
    return () => { window.removeEventListener('mousemove', resize); window.removeEventListener('mouseup', stopResizing); };
  }, [resize, stopResizing]);

  // 스크롤 동기화
  const headerRightRef = useRef(null);
  const bodyLeftRef = useRef(null);
  const bodyRightRef = useRef(null);
  const isAutoScrollingRef = useRef(false);

  const [isScrollLeftEnd, setIsScrollLeftEnd] = useState(false);
  const [isScrollRightEnd, setIsScrollRightEnd] = useState(false);
  const [showTodayButton, setShowTodayButton] = useState(false);

  const updateCapsuleArrows = useCallback(() => {
    if (!bodyRightRef.current) return;
    const { scrollLeft, clientWidth } = bodyRightRef.current;
    const rightPanelLeft = scrollLeft;
    const rightPanelRight = scrollLeft + clientWidth;

    requestAnimationFrame(() => {
      const capsules = document.querySelectorAll('.task-capsule');
      capsules.forEach(capsule => {
        const left = parseFloat(capsule.getAttribute('data-left'));
        const width = parseFloat(capsule.getAttribute('data-width'));
        if (isNaN(left) || isNaN(width)) return;
        const right = left + width;
        const row = capsule.closest('.task-row');
        if (!row) return;

        const leftArrow = row.querySelector('.left-arrow');
        const rightArrow = row.querySelector('.right-arrow');

        if (leftArrow) {
          if (left < rightPanelLeft) { leftArrow.style.display = 'flex'; leftArrow.style.transform = 'translateX(' + (rightPanelLeft + 3) + 'px)'; }
          else leftArrow.style.display = 'none';
        }
        if (rightArrow) {
          if (right > rightPanelRight) { rightArrow.style.display = 'flex'; rightArrow.style.transform = 'translateX(' + (rightPanelRight - 21) + 'px)'; }
          else rightArrow.style.display = 'none';
        }
      });
    });
  }, []);

  useEffect(() => {
    updateCapsuleArrows();
    if (bodyRightRef.current) {
      const { scrollLeft, clientWidth, scrollWidth } = bodyRightRef.current;
      setIsScrollLeftEnd(scrollLeft <= 5);
      setIsScrollRightEnd(scrollLeft + clientWidth >= scrollWidth - 5);
    }
    const handleResize = () => {
      updateCapsuleArrows();
      if (bodyRightRef.current) {
        const { scrollLeft, clientWidth, scrollWidth } = bodyRightRef.current;
        setIsScrollLeftEnd(scrollLeft <= 5);
        setIsScrollRightEnd(scrollLeft + clientWidth >= scrollWidth - 5);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [tasksData, expandedProjects, canvasWidth, updateCapsuleArrows]);

  const handleBodyRightScroll = (e) => {
    const { scrollLeft, scrollTop, scrollWidth, clientWidth } = e.target;
    if (tooltipData) setTooltipData(null);
    if (!isAutoScrollingRef.current) {
      if (headerRightRef.current) headerRightRef.current.scrollLeft = scrollLeft;
      if (bodyLeftRef.current) bodyLeftRef.current.scrollTop = scrollTop;
    } else {
      if (headerRightRef.current) headerRightRef.current.scrollLeft = scrollLeft;
    }
    setIsScrollLeftEnd(scrollLeft <= 5);
    setIsScrollRightEnd(scrollLeft + clientWidth >= scrollWidth - 5);
    updateCapsuleArrows();
    if (isTodayInRange) {
      const isVisible = todayOffset >= scrollLeft - DAY_WIDTH && todayOffset <= scrollLeft + clientWidth;
      setShowTodayButton(!isVisible);
    }
  };

  const handleBodyLeftScroll = (e) => {
    if (isAutoScrollingRef.current) return;
    if (bodyRightRef.current) bodyRightRef.current.scrollTop = e.target.scrollTop;
  };

  const scrollLeftAction = () => {
    if (bodyRightRef.current && bodyRightRef.current.scrollLeft > 10) {
      bodyRightRef.current.scrollBy({ left: -SCROLL_AMOUNT, behavior: 'smooth' });
    } else if (hasMorePast) {
      handleExtendPast();
    }
  };
  const scrollRightAction = () => {
    if (bodyRightRef.current) {
      const { scrollLeft, clientWidth, scrollWidth } = bodyRightRef.current;
      if (scrollLeft + clientWidth < scrollWidth - 10) {
        bodyRightRef.current.scrollBy({ left: SCROLL_AMOUNT, behavior: 'smooth' });
      } else if (hasMoreFuture) {
        handleExtendFuture();
      }
    }
  };

  // 항목별 위치 추적(확장 및 점프) 스크롤 로직
  const handleJumpToTask = (taskStartStr, taskEndStr, direction) => {
    const tStart = new Date(taskStartStr);
    const tEnd = new Date(taskEndStr);

    if (direction === 'start') {
      if (tStart < canvasStart) {
        // 막대가 캔버스보다 과거에 있으면 1달치 추가 조립
        const newStart = new Date(canvasStart);
        newStart.setMonth(newStart.getMonth() - 1);
        setCanvasStart(newStart);
        // 렌더링 후 점프 (150ms 대기하여 DOM 업데이트 보장)
        setTimeout(() => {
          if (!bodyRightRef.current) return;
          const targetDate = tStart < newStart ? newStart : tStart; // 1달만 확장되었으므로 벽에 부딪힐 수 있음
          const offsetDays = Math.floor((targetDate - newStart) / (1000 * 60 * 60 * 24));
          bodyRightRef.current.scrollTo({ left: Math.max(0, offsetDays * DAY_WIDTH - 50), behavior: 'smooth' });
        }, 150);
      } else {
        const offsetDays = Math.floor((tStart - canvasStart) / (1000 * 60 * 60 * 24));
        if (bodyRightRef.current) bodyRightRef.current.scrollTo({ left: Math.max(0, offsetDays * DAY_WIDTH - 50), behavior: 'smooth' });
      }
    } else {
      if (tEnd > canvasEnd) {
        const newEnd = new Date(canvasEnd.getFullYear(), canvasEnd.getMonth() + 2, 0, 23, 59, 59, 999);
        setCanvasEnd(newEnd);
        setTimeout(() => {
          if (!bodyRightRef.current) return;
          const targetDate = tEnd > newEnd ? newEnd : tEnd;
          const offsetDays = Math.floor((targetDate - canvasStart) / (1000 * 60 * 60 * 24));
          bodyRightRef.current.scrollTo({ left: offsetDays * DAY_WIDTH - bodyRightRef.current.clientWidth + 50, behavior: 'smooth' });
        }, 150);
      } else {
        const offsetDays = Math.floor((tEnd - canvasStart) / (1000 * 60 * 60 * 24));
        if (bodyRightRef.current) bodyRightRef.current.scrollTo({ left: offsetDays * DAY_WIDTH - bodyRightRef.current.clientWidth + 50, behavior: 'smooth' });
      }
    }
  };

  // 진행 기간 클릭 시 해당 기간 중앙으로 이동 (범위 밖이면 중간 달 기준 3개월 재설정)
  const scrollToTaskCenter = useCallback((startStr, endStr) => {
    if (!startStr || !endStr || !bodyRightRef.current) return;
    const tStart = new Date(startStr);
    const tEnd = new Date(endStr);
    const midDate = new Date((tStart.getTime() + tEnd.getTime()) / 2);
    const clientWidth = bodyRightRef.current.clientWidth;

    if (midDate >= canvasStart && midDate <= canvasEnd) {
      const offsetDays = Math.floor((midDate - canvasStart) / (1000 * 60 * 60 * 24));
      const targetLeft = Math.max(0, (offsetDays * DAY_WIDTH) - (clientWidth / 2));
      bodyRightRef.current.scrollTo({ left: targetLeft, behavior: 'smooth' });
    } else {
      const year = midDate.getFullYear();
      const month = midDate.getMonth();
      const newStart = new Date(year, month - 1, 1, 0, 0, 0, 0);
      const newEnd = new Date(year, month + 2, 0, 23, 59, 59, 999);
      setCanvasStart(newStart);
      setCanvasEnd(newEnd);
      setTimeout(() => {
        if (!bodyRightRef.current) return;
        const offsetDays = Math.floor((midDate - newStart) / (1000 * 60 * 60 * 24));
        const targetLeft = Math.max(0, (offsetDays * DAY_WIDTH) - (clientWidth / 2));
        bodyRightRef.current.scrollTo({ left: targetLeft, behavior: 'smooth' });
      }, 120);
    }
  }, [canvasStart, canvasEnd]);

  const goToToday = useCallback(() => {
    if (!bodyRightRef.current) return;
    if (isTodayInRange) {
      const clientWidth = bodyRightRef.current.clientWidth;
      bodyRightRef.current.scrollTo({ left: Math.max(0, todayOffset - (clientWidth / 2) + (DAY_WIDTH / 2)), behavior: 'smooth' });
    } else {
      const today = new Date(); today.setHours(0, 0, 0, 0);
      const newStart = new Date(today.getFullYear(), today.getMonth() - 1, 1, 0, 0, 0, 0);
      const newEnd = new Date(today.getFullYear(), today.getMonth() + 2, 0, 23, 59, 59, 999);
      setCanvasStart(newStart);
      setCanvasEnd(newEnd);
      setTimeout(() => {
        if (!bodyRightRef.current) return;
        const offsetDays = Math.floor((today - newStart) / (1000 * 60 * 60 * 24));
        const clientWidth = bodyRightRef.current.clientWidth;
        bodyRightRef.current.scrollTo({ left: Math.max(0, (offsetDays * DAY_WIDTH) - (clientWidth / 2) + (DAY_WIDTH / 2)), behavior: 'smooth' });
      }, 120);
    }
  }, [isTodayInRange, todayOffset]);

  const hasScrolledRef = useRef(false);
  useEffect(() => {
    if (hasScrolledRef.current || tasksData.length === 0) return;
    const timer = setTimeout(() => {
      if (bodyRightRef.current && isTodayInRange) {
        const clientWidth = bodyRightRef.current.clientWidth;
        isAutoScrollingRef.current = true;
        bodyRightRef.current.scrollTo({ left: Math.max(0, todayOffset - (clientWidth / 2) + (DAY_WIDTH / 2)), behavior: 'smooth' });
        setTimeout(() => { isAutoScrollingRef.current = false; }, 800);
        hasScrolledRef.current = true;
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [isTodayInRange, todayOffset, tasksData]);

  // 달력 헤더 생성
  const { months, daysArray } = useMemo(() => {
    const mArr = []; const dArr = []; let cur = new Date(projectStart);
    let currentMonthStr = null;
    let daysInMonth = 0;

    while (cur <= projectEnd) {
      dArr.push(new Date(cur));
      const mStr = cur.getFullYear() + '년 ' + String(cur.getMonth() + 1).padStart(2, '0') + '월';

      if (currentMonthStr !== mStr) {
        if (currentMonthStr !== null) {
          mArr.push({ label: currentMonthStr, days: daysInMonth });
        }
        currentMonthStr = mStr;
        daysInMonth = 1;
      } else {
        daysInMonth++;
      }

      cur.setDate(cur.getDate() + 1);
    }

    if (currentMonthStr !== null) {
      mArr.push({ label: currentMonthStr, days: daysInMonth });
    }

    return { months: mArr, daysArray: dArr };
  }, [projectStart, projectEnd]);

  const getPosition = (start, end) => {
    const s = new Date(start); const e = new Date(end);
    const offsetDays = Math.floor((s - projectStart) / (1000 * 60 * 60 * 24));
    const durationDays = Math.floor((e - s) / (1000 * 60 * 60 * 24)) + 1;
    return { left: offsetDays * DAY_WIDTH, width: durationDays * DAY_WIDTH };
  };

  const getCapsuleColor = (status) => {
    if (status === '완료') return { bg: '#d1e7dd', fill: '#198754' };
    if (status === '진행 중') return { bg: '#cfe2ff', fill: '#0d6efd' };
    return { bg: '#fff3cd', fill: '#ffc107' };
  };

  const [tooltipData, setTooltipData] = useState(null);
  const [popupData, setPopupData] = useState(null);

  const handleMouseEnter = (e, text) => {
    const target = e.currentTarget;
    if (target.scrollWidth > target.clientWidth + 2) {
      const rect = target.getBoundingClientRect();
      setPopupData({ text, top: rect.top, left: rect.left, height: rect.height });
    }
  };
  const handleMouseLeave = () => setPopupData(null);

  const [inlineConfirmData, setInlineConfirmData] = useState(null);
  const dragInfo = useRef({ type: null, projectId: null, taskId: null, startX: 0, originalStart: null, originalEnd: null, originalProgress: 0, hasChanged: false, isParent: false, originalChildTasks: [], woIds: [] });
  const clickTimeout = useRef(null);

  const loadTimelineTasksRef = useRef(loadTimelineTasks);
  useEffect(() => { loadTimelineTasksRef.current = loadTimelineTasks; }, [loadTimelineTasks]);

  // 부모 업무협조 막대 마우스다운 (기간 이동/리사이즈 전용)
  const handleParentMouseDown = (e, projectId, type) => {
    if (inlineConfirmData) { e.stopPropagation(); return; }
    setTooltipData(null); e.stopPropagation();
    const project = tasksData.find(p => p.id === projectId);
    if (!project) return;

    const canEdit = project.tasks?.some(t => t.canEditDate) || isAdminRole || isSuperAdmin;
    if (!canEdit) return;

    const pStart = new Date(project.start);
    const pEnd = new Date(project.end);

    dragInfo.current = {
      isParent: true,
      type,
      projectId,
      taskId: null,
      startX: e.clientX,
      originalStart: pStart,
      originalEnd: pEnd,
      originalProgress: project.progress || 0,
      originalChildTasks: (project.tasks || []).map(t => ({ id: t.id, start: t.start, end: t.end })),
      woIds: Array.from(new Set(
        (project.tasks || [])
          .map(t => t.workOrderId || (t.id && t.id.startsWith('wo_') ? parseInt(t.id.split('_')[1], 10) : null))
          .filter(Boolean)
      )),
      hasChanged: false
    };
    document.body.style.userSelect = 'none';
    if (type === 'move') document.body.style.cursor = 'grabbing';
    else if (type === 'resize-right') document.body.style.cursor = 'e-resize';
    else if (type === 'resize-left') document.body.style.cursor = 'w-resize';

    window.addEventListener('mousemove', handleTaskMouseMove);
    window.addEventListener('mouseup', handleTaskMouseUp);
  };

  const handleTaskMouseDown = (e, projectId, taskId, type) => {
    if (inlineConfirmData) { e.stopPropagation(); return; }
    setTooltipData(null); e.stopPropagation();
    const task = tasksData.find(p => p.id === projectId).tasks.find(t => t.id === taskId);

    // 하위 담당자는 오직 진척도(progress)만 조절 가능 (기간 조절은 부모 막대로 일원화)
    if (type !== 'progress' || !task.canEditProgress) return;

    const tStart = new Date(task.start);
    const tEnd = new Date(task.end);

    dragInfo.current = {
      isParent: false,
      type, projectId, taskId, startX: e.clientX,
      originalStart: tStart, originalEnd: tEnd, originalProgress: task.progress, hasChanged: false
    };
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';

    window.addEventListener('mousemove', handleTaskMouseMove);
    window.addEventListener('mouseup', handleTaskMouseUp);
  };

  const handleTaskMouseMove = useCallback((e) => {
    const info = dragInfo.current; if (!info.type) return;
    info.lastX = e.clientX; info.lastY = e.clientY;
    const diffDays = Math.round((e.clientX - info.startX) / DAY_WIDTH);
    if (Math.abs(e.clientX - info.startX) > 5) info.hasChanged = true;

    setTasksData(prev => {
      const newData = [...prev];
      const pIndex = newData.findIndex(p => p.id === info.projectId);
      if (pIndex === -1) return prev;
      const project = { ...newData[pIndex], tasks: [...newData[pIndex].tasks] };

      // [부모 막대 기간 변경 시] 부모 및 모든 자식 태스크 기간 실시간 일괄 동기화
      if (info.isParent) {
        let nStart = new Date(info.originalStart);
        let nEnd = new Date(info.originalEnd);

        if (info.type === 'move') {
          nStart.setDate(nStart.getDate() + diffDays);
          nEnd.setDate(nEnd.getDate() + diffDays);
        } else if (info.type === 'resize-right') {
          nEnd.setDate(nEnd.getDate() + diffDays);
          if (nEnd < nStart) nEnd = new Date(nStart);
        } else if (info.type === 'resize-left') {
          nStart.setDate(nStart.getDate() + diffDays);
          if (nStart > nEnd) nStart = new Date(nEnd);
        }

        const startStr = nStart.toISOString().split('T')[0];
        const endStr = nEnd.toISOString().split('T')[0];

        project.start = startStr;
        project.end = endStr;
        project.tasks = project.tasks.map(t => ({
          ...t,
          start: startStr,
          end: endStr
        }));

        info.lastCalculated = { start: startStr, end: endStr };
        newData[pIndex] = project;
        return newData;
      }

      // [하위 자식 막대 진척도 변경 시]
      const tIndex = project.tasks.findIndex(t => t.id === info.taskId);
      if (tIndex === -1) return prev;
      const task = { ...project.tasks[tIndex] };

      if (info.type === 'progress') {
        const totalDuration = Math.floor((info.originalEnd - info.originalStart) / (1000 * 60 * 60 * 24)) + 1;
        let newProgress = info.originalProgress + Math.round(((e.clientX - info.startX) / (totalDuration * DAY_WIDTH)) * 100);
        if (newProgress < 0) newProgress = 0; if (newProgress > 100) newProgress = 100;
        task.progress = newProgress;
      }
      info.lastCalculated = { start: task.start, end: task.end, progress: task.progress };
      project.tasks[tIndex] = task; newData[pIndex] = project; return newData;
    });
  }, []);

  const handleTaskMouseUp = useCallback(async (e) => {
    const info = dragInfo.current;
    if (info.hasChanged && info.type && info.lastCalculated) {
      const origStartStr = info.originalStart.toISOString().split('T')[0];
      const origEndStr = info.originalEnd.toISOString().split('T')[0];

      // 1. 부모 막대 기간 변경 완료 시
      if (info.isParent) {
        const isDateDiff = origStartStr !== info.lastCalculated.start || origEndStr !== info.lastCalculated.end;
        if (isDateDiff) {
          const isConfirmed = window.confirm(
            `업무협조 기간을 변경하시겠습니까?\n배정된 모든 담당자의 일정도 일괄 변경됩니다.\n\n• 변경 전: ${origStartStr} ~ ${origEndStr}\n• 변경 후: ${info.lastCalculated.start} ~ ${info.lastCalculated.end}`
          );

          if (isConfirmed) {
            try {
              const woIds = (info.woIds && info.woIds.length > 0) ? info.woIds : [];
              if (woIds.length > 0) {
                await Promise.all(woIds.map(id => workOrderApi.updateTimelineSchedule(id, {
                  start_date: info.lastCalculated.start,
                  due_date: info.lastCalculated.end
                })));
                if (loadTimelineTasksRef.current) loadTimelineTasksRef.current();
              }
            } catch (err) {
              console.error('업무협조 기간 업데이트 실패:', err);
              alert(err.message || '기간 변경에 실패했습니다.');
              if (loadTimelineTasksRef.current) loadTimelineTasksRef.current();
            }
          } else {
            // 취소 시 원래 일정으로 복원
            setTasksData(prev => {
              const newData = [...prev];
              const pIndex = newData.findIndex(p => p.id === info.projectId);
              if (pIndex !== -1) {
                const project = { ...newData[pIndex], tasks: [...newData[pIndex].tasks] };
                project.start = origStartStr;
                project.end = origEndStr;
                project.tasks = project.tasks.map(t => {
                  const orig = (info.originalChildTasks || []).find(o => o.id === t.id);
                  return orig ? { ...t, start: orig.start, end: orig.end } : t;
                });
                newData[pIndex] = project;
              }
              return newData;
            });
          }
        }
      } else {
        // 2. 기존 하위 자식 진척도 조절 완료 시 (인라인 컨펌 모달)
        const isExactlySame =
          origStartStr === info.lastCalculated.start &&
          origEndStr === info.lastCalculated.end &&
          info.originalProgress === info.lastCalculated.progress;

        if (!isExactlySame) {
          setInlineConfirmData({
            type: info.type, projectId: info.projectId, taskId: info.taskId, clientX: info.lastX, clientY: info.lastY,
            originalStart: origStartStr, originalEnd: origEndStr, originalProgress: info.originalProgress,
            newStart: info.lastCalculated.start, newEnd: info.lastCalculated.end, newProgress: info.lastCalculated.progress
          });
        }
      }
    }
    document.body.style.userSelect = ''; document.body.style.cursor = '';
    window.removeEventListener('mousemove', handleTaskMouseMove); window.removeEventListener('mouseup', handleTaskMouseUp);
    setTimeout(() => { dragInfo.current.type = null; dragInfo.current.hasChanged = false; dragInfo.current.isParent = false; setTasksData(prev => [...prev]); }, 50);
  }, [handleTaskMouseMove]);

  // 컴포넌트 언마운트 시 드래그 리스너 안전 해제
  useEffect(() => {
    return () => {
      window.removeEventListener('mousemove', handleTaskMouseMove);
      window.removeEventListener('mouseup', handleTaskMouseUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
  }, [handleTaskMouseMove, handleTaskMouseUp]);

  const handleInlineConfirmAction = async () => {
    if (inlineConfirmData && inlineConfirmData.taskId) {
      try {
        const parts = inlineConfirmData.taskId.split('_');
        const woId = parts[1];
        if (woId) {
          const payload = {};
          if (inlineConfirmData.type === 'progress') {
            payload.progress_rate = inlineConfirmData.newProgress;
            if (parts[2]) payload.assignee_id = parseInt(parts[2], 10);
          } else {
            payload.start_date = inlineConfirmData.newStart;
            payload.due_date = inlineConfirmData.newEnd;
          }
          await workOrderApi.updateTimelineSchedule(woId, payload);
        }
      } catch (err) {
        console.error('타임라인 일정 업데이트 실패:', err);
        handleInlineCancelAction();
        return;
      }
    }
    setInlineConfirmData(null);
  };
  const handleInlineCancelAction = () => {
    setTasksData(prev => {
      const newData = [...prev];
      const pIndex = newData.findIndex(p => p.id === inlineConfirmData.projectId);
      const project = { ...newData[pIndex], tasks: [...newData[pIndex].tasks] };
      const tIndex = project.tasks.findIndex(t => t.id === inlineConfirmData.taskId);
      project.tasks[tIndex] = { ...project.tasks[tIndex], start: inlineConfirmData.originalStart, end: inlineConfirmData.originalEnd, progress: inlineConfirmData.originalProgress };
      newData[pIndex] = project; return newData;
    });
    setInlineConfirmData(null);
  };

  return (
    <div className="d-flex flex-column" style={{ position: 'absolute', top: 0, bottom: 0, left: 0, right: 0, backgroundColor: '#f4f5f7', padding: '30px', overflow: 'hidden', boxSizing: 'border-box' }} onClick={() => { if (tooltipData) setTooltipData(null); }}>
      <style>{customStyles}</style>

      {/* [ 상단 헤더 영역 - 프리미엄 딥 다크 네이비 카드 테마 ] */}
      <div
        className="d-flex flex-column mb-3 flex-shrink-0"
        style={{
          background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
          borderRadius: '16px',
          boxShadow: '0 8px 24px -4px rgba(15, 23, 42, 0.22)',
          color: '#ffffff',
          zIndex: 10
        }}
      >
        {/* 타이틀 및 메인 헤더 */}
        <div className="d-flex align-items-center justify-content-between px-4 py-3">
          <div className="d-flex align-items-center gap-3">
            <div>
              <div className="d-flex align-items-center gap-2 mb-1">
                <i className="fas fa-project-diagram" style={{ color: '#60a5fa', fontSize: '18px' }} />
                <h4 className="mb-0 fw-bold text-white" style={{ letterSpacing: '-0.5px' }}>업무협조 통합 타임라인</h4>
              </div>
              <div className="d-flex align-items-center mt-1" style={{ fontSize: '13px', color: '#94a3b8' }}>
                <i className="far fa-calendar-alt me-1 opacity-75" /> {projectStart.getFullYear()}.{String(projectStart.getMonth() + 1).padStart(2, '0')} ~ {projectEnd.getFullYear()}.{String(projectEnd.getMonth() + 1).padStart(2, '0')}
                <div className="border-start mx-3 opacity-25" style={{ height: '14px', borderColor: 'rgba(255, 255, 255, 0.2)' }} />
                <i className="fas fa-chart-line me-1 opacity-75 text-info" /> 총 진척도 <strong className="text-white ms-1">{totalProgress}%</strong>
                <div className="progress ms-2 rounded-pill border-0" style={{ width: '120px', height: '6px', backgroundColor: 'rgba(255, 255, 255, 0.12)' }}>
                  <div className="progress-bar" style={{ width: `${totalProgress}%`, backgroundColor: totalProgress === 100 ? '#10b981' : '#3b82f6', borderRadius: '3px' }} />
                </div>
              </div>
            </div>
          </div>
          <div className="d-flex align-items-center gap-2">
            {isAdminRole && (
              <label
                className="d-flex align-items-center gap-2 m-0"
                style={{
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  color: '#e2e8f0',
                  cursor: 'pointer',
                  userSelect: 'none',
                  marginRight: '6px'
                }}
              >
                <input
                  type="checkbox"
                  className="form-check-input m-0"
                  checked={adminAllChecked}
                  onChange={(e) => setAdminAllChecked(e.target.checked)}
                  style={{
                    width: '16px',
                    height: '16px',
                    cursor: 'pointer',
                    accentColor: '#3b82f6'
                  }}
                />
                <span>관리자 전체보기</span>
              </label>
            )}
            <button
              className="btn btn-sm fw-bold d-flex align-items-center gap-2"
              onClick={loadTimelineTasks}
              style={{
                height: '36px',
                padding: '0 14px',
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '10px',
                color: '#e2e8f0',
                fontSize: '13px'
              }}
            >
              <i className="fas fa-sync-alt" style={{ fontSize: '0.8rem', color: '#93c5fd' }} /> 새로고침
            </button>
          </div>
        </div>
      </div>

      {/* [ 타임라인 메인 렌더링 뷰 ] */}
      <div className="flex-grow-1 bg-white rounded-3 shadow-sm border border-light d-flex flex-column position-relative" style={{ minHeight: 0, overflow: 'hidden' }}>

        {/* ============================================================== */}
        {/* 업무지시 필터 툴바 (부서, 담당자, 상태, 보기 모드 등) */}
        {/* ============================================================== */}
        <div className="d-flex align-items-center justify-content-between px-3 py-2 border-bottom border-secondary border-opacity-10" style={{ flexShrink: 0, background: 'linear-gradient(to bottom, #ffffff, #f8f9fa)' }}>
          <div className="d-flex align-items-center gap-2">

            {/* 완전히 분리된 카테고리(업무 성격) 선택 박스 */}
            <div className="d-flex align-items-center bg-white rounded-3 border p-1 shadow-sm">
              <select className="modern-select fw-bold text-primary py-0" style={{ width: '125px', height: '26px', border: 'none', backgroundPositionY: 'center' }} value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
                <option value="received">요청받은 업무</option>
                <option value="sent">요청한 업무</option>
                <option value="cc">참조 업무</option>
              </select>
            </div>

            {/* 두 하얀 박스 사이 바깥 허공에 있는 세로선 */}
            <div className="border-start border-secondary border-opacity-25 mx-2" style={{ height: '24px' }}></div>

            <div className="d-flex align-items-center gap-2 bg-white rounded-3 border p-1 shadow-sm">
              <CustomMultiSelect
                title="부서"
                iconClass="fas fa-building"
                type="dept"
                options={deptOptions.map((d, i) => ({ label: d, value: d, idx: i }))}
                selectedValues={filterDept}
                onChange={setFilterDept}
              />
              <div className="border-start border-secondary border-opacity-25" style={{ height: '16px' }}></div>

              <CustomMultiSelect
                title={filterCategory === 'received' ? '요청자' : '담당자'}
                iconClass=""
                type="user"
                options={(filterDept.length === 0 ? userOptions : userOptions.filter(u => filterDept.some(dept => u.value.includes(dept)))).map((u, i) => ({ ...u, idx: i }))}
                selectedValues={filterUser}
                onChange={setFilterUser}
              />
              <div className="border-start border-secondary border-opacity-25" style={{ height: '16px' }}></div>

              <CustomMultiSelect
                title="상태"
                iconClass="fas fa-tasks"
                type="status"
                options={[
                  { label: '대기', value: '대기' },
                  { label: '진행중', value: '진행중' },
                  { label: '검토대기', value: '검토대기' },
                  { label: '접수지연', value: '접수지연' },
                  { label: '진행지연', value: '진행지연' },
                  { label: '중단', value: '중단' },
                  { label: '완료', value: '완료' }
                ]}
                selectedValues={filterStatus}
                onChange={setFilterStatus}
              />
            </div>

            {filterCategory !== 'received' && (
              <>
                <div className="border-start border-secondary border-opacity-25 mx-2" style={{ height: '24px' }}></div>
                <div className="view-mode-switch shadow-sm border border-secondary border-opacity-10">
                  <button className={`view-mode-btn ${viewMode === 'byTask' ? 'active' : ''}`} onClick={() => setViewMode('byTask')}>업무명별</button>
                  <button className={`view-mode-btn ${viewMode === 'byUser' ? 'active' : ''}`} onClick={() => setViewMode('byUser')}>담당자별</button>
                </div>
              </>
            )}

            <div className="border-start border-secondary border-opacity-25 mx-2" style={{ height: '24px' }}></div>

            <button className="btn btn-sm btn-secondary text-white shadow-sm d-flex align-items-center gap-1 px-3 fw-bold transition-all" onClick={handleResetFilters} title="필터 및 모드 초기화" style={{ borderRadius: '6px', fontSize: '13px', letterSpacing: '0.5px' }}>
              <i className="fas fa-redo-alt" style={{ fontSize: '12px' }}></i> 초기화
            </button>

            {/* 초기화와 가이드 사이 세로 구분선 */}
            <div className="border-start border-secondary border-opacity-25 mx-2" style={{ height: '24px' }}></div>

            {/* 도움말 가이드 아이콘 & 3탭 팝오버 */}
            <div className="position-relative d-flex align-items-center" ref={guideContainerRef}>
              <button
                type="button"
                className={`btn btn-sm shadow-sm d-flex align-items-center justify-content-center transition-all ${showGuidePopover ? 'btn-primary text-white border-primary' : 'border'}`}
                onClick={() => {
                  if (!showGuidePopover) setGuideTab('period');
                  setShowGuidePopover(!showGuidePopover);
                }}
                title="타임라인 이용 가이드"
                style={{
                  width: '28px',
                  height: '28px',
                  borderRadius: '5px',
                  padding: 0,
                  boxSizing: 'border-box',
                  backgroundColor: showGuidePopover ? undefined : '#fef3c7',
                  color: showGuidePopover ? undefined : '#d97706',
                  borderColor: showGuidePopover ? undefined : '#fde68a'
                }}
              >
                <i className="fas fa-info" style={{ fontSize: '12px', fontWeight: 'bold' }}></i>
              </button>

              {/* 가이드 탭 팝오버 */}
              {showGuidePopover && (
                <div
                  className="position-absolute bg-white rounded-3 shadow-lg border overflow-hidden"
                  style={{
                    top: '100%',
                    left: 0,
                    marginTop: '8px',
                    width: '480px',
                    zIndex: 1050,
                    cursor: 'default',
                    boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)'
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* 팝오버 헤더 */}
                  <div className="d-flex justify-content-between align-items-center px-3 bg-light border-bottom" style={{ paddingTop: '11px', paddingBottom: '11px' }}>
                    <span className="fw-bold text-dark d-flex align-items-center gap-2" style={{ fontSize: '15px' }}>
                      <span className="badge bg-primary bg-opacity-10 text-primary p-1 rounded-circle" style={{ width: '22px', height: '22px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                        <i className="fas fa-info" style={{ fontSize: '11px' }}></i>
                      </span>
                      타임라인 이용 가이드
                    </span>
                    <button
                      type="button"
                      className="btn-close shadow-none"
                      style={{ fontSize: '10px' }}
                      onClick={() => setShowGuidePopover(false)}
                    ></button>
                  </div>

                  {/* 탭 네비게이션 */}
                  <div className="d-flex border-bottom bg-white px-2 pt-2 gap-1">
                    <button
                      type="button"
                      className={`btn btn-sm border-0 pb-2 px-2 fw-bold position-relative transition-all ${guideTab === 'period' ? 'text-primary' : 'text-muted hover-text-dark'}`}
                      style={{ fontSize: '13px', whiteSpace: 'nowrap' }}
                      onClick={() => setGuideTab('period')}
                    >
                      <i className="far fa-calendar-alt me-1"></i> 기간·이동
                      {guideTab === 'period' && (
                        <div className="position-absolute bottom-0 start-0 w-100 bg-primary" style={{ height: '2px', borderRadius: '2px 2px 0 0' }}></div>
                      )}
                    </button>
                    <button
                      type="button"
                      className={`btn btn-sm border-0 pb-2 px-2 fw-bold position-relative transition-all ${guideTab === 'schedule' ? 'text-primary' : 'text-muted hover-text-dark'}`}
                      style={{ fontSize: '13px', whiteSpace: 'nowrap' }}
                      onClick={() => setGuideTab('schedule')}
                    >
                      <i className="fas fa-lock me-1"></i> 기간 변경
                      {guideTab === 'schedule' && (
                        <div className="position-absolute bottom-0 start-0 w-100 bg-primary" style={{ height: '2px', borderRadius: '2px 2px 0 0' }}></div>
                      )}
                    </button>
                    <button
                      type="button"
                      className={`btn btn-sm border-0 pb-2 px-2 fw-bold position-relative transition-all ${guideTab === 'progress' ? 'text-primary' : 'text-muted hover-text-dark'}`}
                      style={{ fontSize: '13px', whiteSpace: 'nowrap' }}
                      onClick={() => setGuideTab('progress')}
                    >
                      <i className="fas fa-chart-line me-1"></i> 진척도 조절
                      {guideTab === 'progress' && (
                        <div className="position-absolute bottom-0 start-0 w-100 bg-primary" style={{ height: '2px', borderRadius: '2px 2px 0 0' }}></div>
                      )}
                    </button>
                    <button
                      type="button"
                      className={`btn btn-sm border-0 pb-2 px-2 fw-bold position-relative transition-all ${guideTab === 'status' ? 'text-primary' : 'text-muted hover-text-dark'}`}
                      style={{ fontSize: '13px', whiteSpace: 'nowrap' }}
                      onClick={() => setGuideTab('status')}
                    >
                      <i className="fas fa-palette me-1"></i> 상태·색상
                      {guideTab === 'status' && (
                        <div className="position-absolute bottom-0 start-0 w-100 bg-primary" style={{ height: '2px', borderRadius: '2px 2px 0 0' }}></div>
                      )}
                    </button>
                  </div>

                  {/* 탭 본문 내용 */}
                  <div className="p-3" style={{ fontSize: '12px', lineHeight: '1.6', minHeight: '190px' }}>
                    {guideTab === 'period' && (
                      <div className="d-flex flex-column gap-2">
                        <div className="bg-light p-2 rounded-2 border border-secondary border-opacity-10">
                          <div className="fw-bold text-dark d-flex align-items-center gap-1 mb-1">
                            <span className="badge bg-secondary" style={{ fontSize: '10px' }}>기본 범위</span> 3개월 업무 표시
                          </div>
                          <div className="text-muted" style={{ fontSize: '11.5px' }}>
                            기준월을 중심으로 <strong className="text-dark">[전달 - 이번 달 - 다음 달]</strong> 총 3개월 범위에 걸쳐 있는 업무협조만 조회되어 표시됩니다.
                          </div>
                        </div>

                        <div className="bg-light p-2 rounded-2 border border-secondary border-opacity-10">
                          <div className="fw-bold text-dark d-flex align-items-center gap-1 mb-1">
                            <span className="badge bg-success" style={{ fontSize: '10px' }}>원클릭 이동</span> 진행기간 클릭 시 중앙 이동
                          </div>
                          <div className="text-muted" style={{ fontSize: '11.5px' }}>
                            좌측 목록에서 세부 업무의 <strong className="text-dark">진행기간(날짜)을 클릭</strong>하면, 해당 업무 기간의 한가운데로 타임라인이 부드럽게 이동합니다. (3개월 화면 밖인 경우 해당 달 기준 3개월로 자동 전환)
                          </div>
                        </div>

                        <div className="bg-light p-2 rounded-2 border border-secondary border-opacity-10">
                          <div className="fw-bold text-dark d-flex align-items-center gap-1 mb-1">
                            <span className="badge bg-primary" style={{ fontSize: '10px' }}>달력 확장</span> 상단 달력의 좌우 화살표 (<i className="fas fa-arrow-left" style={{ fontSize: '9px' }}></i> / <i className="fas fa-arrow-right" style={{ fontSize: '9px' }}></i>)
                          </div>
                          <div className="text-muted" style={{ fontSize: '11.5px' }}>
                            현재 3개월 화면 밖(과거 또는 미래)에 <strong className="text-dark">실제 업무협조가 존재할 때만</strong> 달력 양옆에 화살표가 나타나며, 클릭 시 해당 방향으로 캘린더를 1달씩 확장합니다.
                          </div>
                        </div>

                        <div className="bg-light p-2 rounded-2 border border-secondary border-opacity-10">
                          <div className="fw-bold text-dark d-flex align-items-center gap-1 mb-1">
                            <span className="badge bg-info text-dark" style={{ fontSize: '10px' }}>빠른 이동</span> 상단 연월 선택 및 초기화
                          </div>
                          <div className="text-muted" style={{ fontSize: '11.5px' }}>
                            상단 달력의 <strong className="text-dark">연월을 클릭하면 월 선택 팝업</strong>이 열려 원하는 달로 즉시 이동할 수 있으며, <strong className="text-dark">[초기화]</strong> 버튼을 누르면 언제든 오늘 기준 3개월로 깨끗하게 복귀합니다.
                          </div>
                        </div>
                      </div>
                    )}

                    {guideTab === 'schedule' && (
                      <div className="d-flex flex-column gap-2">
                        <div className="bg-light p-2 rounded-2 border border-secondary border-opacity-10">
                          <div className="fw-bold text-dark d-flex align-items-center gap-1 mb-1">
                            <span className="badge bg-success" style={{ fontSize: '10px' }}>변경 위치 및 권한</span> 상위 업무협조 바 (요청자/관리자)
                          </div>
                          <div className="text-muted" style={{ fontSize: '11.5px' }}>
                            상위의 <strong className="text-dark">청록색 업무협조 바</strong>에서만 기간 변경이 가능합니다. <strong className="text-dark">요청자</strong> 또는 <strong className="text-dark">관리자</strong>만 바를 드래그하여 일정을 이동하거나, 좌·우 가장자리를 늘려 시작일·마감일을 조절할 수 있습니다.
                          </div>
                        </div>

                        <div className="bg-light p-2 rounded-2 border border-secondary border-opacity-10">
                          <div className="fw-bold text-dark d-flex align-items-center gap-1 mb-1">
                            <span className="badge bg-primary" style={{ fontSize: '10px' }}>일괄 동기화</span> 하위 모든 담당자 일정 연동
                          </div>
                          <div className="text-muted" style={{ fontSize: '11.5px' }}>
                            상위 바 드래그 완료 시 확인창이 나타나며, 승인 시 <strong className="text-dark">배정된 모든 담당자의 일정도 일괄 동기화</strong>되어 변경됩니다.
                          </div>
                        </div>

                        <div className="bg-light p-2 rounded-2 border border-secondary border-opacity-10">
                          <div className="fw-bold text-dark d-flex align-items-center gap-1 mb-1">
                            <span className="badge bg-danger" style={{ fontSize: '10px' }}>하위 바 변경 차단</span> 개별 담당자 일정 변경 불가
                          </div>
                          <div className="text-muted" style={{ fontSize: '11.5px' }}>
                            하위의 개별 담당자 바에서는 <strong className="text-dark">요청자/관리자를 포함하여 누구도 기간을 변경할 수 없습니다</strong> (원천 차단). 담당자 바에서는 오직 진척도 조절만 가능합니다.
                          </div>
                        </div>
                      </div>
                    )}

                    {guideTab === 'progress' && (
                      <div className="d-flex flex-column gap-2">
                        <div className="bg-light p-2 rounded-2 border border-secondary border-opacity-10">
                          <div className="fw-bold text-dark d-flex align-items-center gap-1 mb-1">
                            <span className="badge bg-success" style={{ fontSize: '10px' }}>조절 위치 및 권한</span> 하위 담당자 바 (담당자/요청자/관리자)
                          </div>
                          <div className="text-muted" style={{ fontSize: '11.5px' }}>
                            진척도 조절은 <strong className="text-dark">하위 세부 업무(담당자 바)</strong>의 우측 조절 핸들(<strong>||</strong>)을 드래그하여 조절합니다. (상위 청록색 바는 전체 평균 진척도 조회 전용)
                          </div>
                        </div>

                        <div className="bg-light p-2 rounded-2 border border-secondary border-opacity-10">
                          <div className="fw-bold text-dark d-flex align-items-center gap-1 mb-1">
                            <span className="badge bg-primary" style={{ fontSize: '10px' }}>조절 가능 상태</span> 진행중 및 진행지연
                          </div>
                          <div className="text-muted" style={{ fontSize: '11.5px' }}>
                            업무 상태가 <span className="badge bg-primary px-1" style={{ fontSize: '9px' }}>진행중</span> 또는 <span className="badge bg-danger px-1" style={{ fontSize: '9px' }}>진행지연</span>일 때만 진척도를 자유롭게 조절할 수 있습니다.
                          </div>
                        </div>

                        <div className="bg-light p-2 rounded-2 border border-secondary border-opacity-10">
                          <div className="fw-bold text-dark d-flex align-items-center gap-1 mb-1">
                            <span className="badge bg-secondary" style={{ fontSize: '10px' }}>조절 불가 상태</span> 대기 / 검토 / 완료 / 중단
                          </div>
                          <div className="text-muted" style={{ fontSize: '11.5px' }}>
                            아직 접수하지 않은 상태(<strong className="text-dark">대기, 접수지연</strong>)는 0%로 고정되며, 마감 또는 확인 대기 상태(<strong className="text-dark">검토대기, 완료, 중단</strong>)는 진척도를 수정할 수 없습니다.
                          </div>
                        </div>
                      </div>
                    )}

                    {guideTab === 'status' && (
                      <div className="d-flex flex-column gap-1" style={{ fontSize: '11.5px' }}>
                        <div className="row g-2">
                          <div className="col-6">
                            <div className="p-2 rounded-2 border bg-light h-100 border-secondary border-opacity-10">
                              <div className="d-flex align-items-center gap-1 mb-1">
                                <span className="badge bg-primary px-1.5 py-0.5" style={{ fontSize: '10px' }}>진행중</span>
                                <span className="fw-bold text-dark" style={{ fontSize: '11px' }}>파란색 바</span>
                              </div>
                              <div className="text-muted" style={{ fontSize: '11px', lineHeight: '1.4' }}>
                                담당자가 업무를 접수하여 정상 진행 중인 상태 (진척도 조절 가능)
                              </div>
                            </div>
                          </div>
                          <div className="col-6">
                            <div className="p-2 rounded-2 border bg-light h-100 border-secondary border-opacity-10">
                              <div className="d-flex align-items-center gap-1 mb-1">
                                <span className="badge bg-danger px-1.5 py-0.5" style={{ fontSize: '10px' }}>진행지연</span>
                                <span className="fw-bold text-dark" style={{ fontSize: '11px' }}>빨간색 바</span>
                              </div>
                              <div className="text-muted" style={{ fontSize: '11px', lineHeight: '1.4' }}>
                                마감 예정일이 지났으나 완료되지 않은 상태 (진척도 조절 가능)
                              </div>
                            </div>
                          </div>
                          <div className="col-6">
                            <div className="p-2 rounded-2 border bg-light h-100 border-secondary border-opacity-10">
                              <div className="d-flex align-items-center gap-1 mb-1">
                                <span className="badge bg-warning text-dark px-1.5 py-0.5" style={{ fontSize: '10px' }}>검토대기</span>
                                <span className="fw-bold text-dark" style={{ fontSize: '11px' }}>주황/살구 바</span>
                              </div>
                              <div className="text-muted" style={{ fontSize: '11px', lineHeight: '1.4' }}>
                                담당자가 처리결과를 제출하여 요청자의 최종 확인을 기다리는 상태
                              </div>
                            </div>
                          </div>
                          <div className="col-6">
                            <div className="p-2 rounded-2 border bg-light h-100 border-secondary border-opacity-10">
                              <div className="d-flex align-items-center gap-1 mb-1">
                                <span className="badge bg-secondary px-1.5 py-0.5" style={{ fontSize: '10px' }}>대기/접수지연</span>
                                <span className="fw-bold text-dark" style={{ fontSize: '11px' }}>회색 바</span>
                              </div>
                              <div className="text-muted" style={{ fontSize: '11px', lineHeight: '1.4' }}>
                                담당자가 아직 업무를 접수하지 않은 초기 상태 (접수 후 착수)
                              </div>
                            </div>
                          </div>
                          <div className="col-6">
                            <div className="p-2 rounded-2 border bg-light h-100 border-secondary border-opacity-10">
                              <div className="d-flex align-items-center gap-1 mb-1">
                                <span className="badge bg-success px-1.5 py-0.5" style={{ fontSize: '10px' }}>완료</span>
                                <span className="fw-bold text-dark" style={{ fontSize: '11px' }}>녹색 바</span>
                              </div>
                              <div className="text-muted" style={{ fontSize: '11px', lineHeight: '1.4' }}>
                                요청자가 최종 검토 및 확인을 마친 상태 (100% 마감)
                              </div>
                            </div>
                          </div>
                          <div className="col-6">
                            <div className="p-2 rounded-2 border bg-light h-100 border-secondary border-opacity-10">
                              <div className="d-flex align-items-center gap-1 mb-1">
                                <span className="badge bg-dark px-1.5 py-0.5" style={{ fontSize: '10px' }}>중단</span>
                                <span className="fw-bold text-dark" style={{ fontSize: '11px' }}>어두운 회색 바</span>
                              </div>
                              <div className="text-muted" style={{ fontSize: '11px', lineHeight: '1.4' }}>
                                요청자 또는 관리자가 업무협조를 중도 취소하여 종료한 상태
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* 팝오버 푸터 */}
                  <div className="px-3 py-2 bg-light border-top d-flex justify-content-between align-items-center">
                    <span className="text-muted" style={{ fontSize: '11.5px' }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="#0d6efd" className="me-1" style={{ verticalAlign: '-2px', display: 'inline-block' }}>
                        <path d="M10 2a2 2 0 0 0-2 2v7.59l-1.3-1.3a2 2 0 0 0-2.82 2.83l4.12 4.12A6 6 0 0 0 12.24 19H15a6 6 0 0 0 6-6V9a2 2 0 0 0-2-2h-1a2 2 0 0 0-2-2h-1a2 2 0 0 0-2-2h-3z" />
                      </svg>
                      좌측 세부 업무명 클릭 시 상세창 열림 / 막대 클릭 시 상태 툴팁 확인
                    </span>
                    <button
                      type="button"
                      className="btn btn-sm btn-white border border-primary shadow-sm py-0 px-2 text-primary fw-bold hover-bg-primary hover-text-white transition-all"
                      style={{ fontSize: '11.5px', height: '24px', borderRadius: '4px' }}
                      onClick={() => setShowGuidePopover(false)}
                    >
                      닫기
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="d-flex align-items-center gap-2">
            <div className="d-flex align-items-center bg-white border rounded-pill shadow-sm p-1">
              {hasMorePast ? (
                <button className="btn btn-sm text-primary hover-bg-primary hover-text-white border-0 rounded-circle d-flex align-items-center justify-content-center"
                  onClick={handleExtendPast}
                  title="과거 데이터가 있습니다. 클릭 시 이전 달 캘린더를 1달 확장합니다."
                  style={{ width: '26px', height: '26px', transition: 'all 0.2s', backgroundColor: 'rgba(13, 110, 253, 0.1)' }}>
                  <i className="fas fa-arrow-left" style={{ fontSize: '12px', fontWeight: 'bold' }}></i>
                </button>
              ) : (
                <div style={{ width: '26px' }}></div>
              )}
              <div
                ref={monthPickerContainerRef}
                className="fw-bolder text-dark px-3 text-center position-relative hover-opacity"
                style={{ minWidth: '160px', fontSize: '13px', letterSpacing: '0.5px', cursor: 'pointer', userSelect: 'none' }}
                onClick={() => {
                  if (!showMonthPicker) setPickerYear(projectStart.getFullYear());
                  setShowMonthPicker(!showMonthPicker);
                }}
              >
                <i className="far fa-calendar-alt text-primary opacity-75 me-2"></i>
                {projectStart.getFullYear()}.{String(projectStart.getMonth() + 1).padStart(2, '0')} ~ {projectEnd.getFullYear()}.{String(projectEnd.getMonth() + 1).padStart(2, '0')}

                {/* 커스텀 월 선택기 (Popover) - 디자인 개선판 */}
                {showMonthPicker && (
                  <div className="position-absolute bg-white rounded-4 shadow-lg border p-0 overflow-hidden" style={{ top: '100%', left: '50%', transform: 'translateX(-50%)', marginTop: '10px', zIndex: 1000, width: '230px', cursor: 'default' }} onClick={(e) => e.stopPropagation()}>
                    {/* 연도 이동 헤더 (배경색 분리) */}
                    <div className="d-flex justify-content-between align-items-center px-3 py-2 bg-light border-bottom border-secondary border-opacity-10">
                      <button className="btn btn-sm btn-white border rounded-circle d-flex align-items-center justify-content-center shadow-sm" style={{ width: '26px', height: '26px' }} onClick={() => setPickerYear(y => y - 1)}><i className="fas fa-chevron-left text-secondary" style={{ fontSize: '10px' }}></i></button>
                      <span className="fw-bolder fs-6 text-dark" style={{ letterSpacing: '-0.5px' }}>{pickerYear}년</span>
                      <button className="btn btn-sm btn-white border rounded-circle d-flex align-items-center justify-content-center shadow-sm" style={{ width: '26px', height: '26px' }} onClick={() => setPickerYear(y => y + 1)}><i className="fas fa-chevron-right text-secondary" style={{ fontSize: '10px' }}></i></button>
                    </div>
                    {/* 1~12월 그리드 (테두리 디자인) */}
                    <div className="row g-2 p-3">
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(m => {
                        // 캔버스 3개월 중 정중앙의 달을 Focus Month로 계산하여 하이라이트
                        const focusDate = new Date(projectStart.getFullYear(), projectStart.getMonth() + 1, 1);
                        const isCurrent = pickerYear === focusDate.getFullYear() && (m - 1) === focusDate.getMonth();
                        const hasDataInMonth = currentActiveMonths.includes(m);
                        return (
                          <div className="col-3" key={m}>
                            <div
                              className={`text-center py-2 rounded-3 fw-bold transition-all border position-relative ${isCurrent ? 'bg-primary text-white border-primary shadow' : 'bg-transparent text-secondary border-secondary border-opacity-10 hover-bg-primary hover-bg-opacity-10 hover-text-primary hover-border-primary hover-border-opacity-25'}`}
                              style={{ fontSize: '12px', cursor: 'pointer' }}
                              onClick={(e) => {
                                e.stopPropagation();
                                const targetMonthIdx = m - 1;
                                const newStart = new Date(pickerYear, targetMonthIdx - 1, 1, 0, 0, 0, 0);
                                setCanvasStart(newStart);
                                const newEnd = new Date(pickerYear, targetMonthIdx + 2, 0, 23, 59, 59, 999);
                                setCanvasEnd(newEnd);
                                setShowMonthPicker(false);
                              }}
                            >
                              {m}월
                              {hasDataInMonth && (
                                <span
                                  className="position-absolute rounded-circle shadow-sm"
                                  style={{
                                    top: '4px',
                                    right: '4px',
                                    width: '5px',
                                    height: '5px',
                                    backgroundColor: isCurrent ? '#ffffff' : '#0d6efd',
                                    pointerEvents: 'none'
                                  }}
                                  title="업무 있음"
                                />
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
              {hasMoreFuture ? (
                <button className="btn btn-sm text-primary hover-bg-primary hover-text-white border-0 rounded-circle d-flex align-items-center justify-content-center"
                  onClick={handleExtendFuture}
                  title="미래 데이터가 있습니다. 클릭 시 다음 달 캘린더를 1달 확장합니다."
                  style={{ width: '26px', height: '26px', transition: 'all 0.2s', backgroundColor: 'rgba(13, 110, 253, 0.1)' }}>
                  <i className="fas fa-arrow-right" style={{ fontSize: '12px', fontWeight: 'bold' }}></i>
                </button>
              ) : (
                <div style={{ width: '26px' }}></div>
              )}
            </div>
            <button className="btn btn-sm btn-primary rounded-pill px-3 fw-bold shadow-sm d-flex align-items-center gap-1 ms-1" style={{ fontSize: '12px' }} onClick={goToToday}>
              <i className="fas fa-crosshairs"></i> 이번 달
            </button>
          </div>
        </div>

        {/* 4분할 래퍼 */}
        <div className="flex-grow-1 d-flex flex-column position-relative" ref={containerRef} style={{ minHeight: 0 }}>

          <div className="resizer-handle" onMouseDown={startResizing} title="드래그하여 크기 조절" style={{ width: '8px', position: 'absolute', left: (listWidth - 4) + 'px', top: 0, bottom: 0, zIndex: 100, background: 'transparent' }} />

          {/* 상단 헤더 래퍼 */}
          <div className="d-flex border-top border-bottom border-secondary border-opacity-25 bg-white" style={{ height: '60px', flexShrink: 0, zIndex: 20, boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>

            {/* 좌측 트리 헤더 */}
            <div className="position-relative border-end border-secondary border-opacity-25 overflow-hidden" style={{ width: listWidth + 'px', flexShrink: 0, boxSizing: 'border-box' }}>
              <div className="d-flex align-items-center bg-light h-100" style={{ width: Math.max(420, listWidth) + 'px' }}>
                <div className="ps-3 pe-2 fw-bolder text-secondary fs-8 d-flex justify-content-between align-items-center" style={{ width: 'calc(100% - 214px)' }}>
                  <span style={{ fontSize: '12px' }}>업무명</span>
                  <div className="d-flex gap-1">
                    <div className="d-flex align-items-center justify-content-center bg-white border rounded shadow-sm" onClick={expandAll} title="전체 펼치기" style={{ width: '20px', height: '20px', cursor: 'pointer' }}><i className="fas fa-plus" style={{ fontSize: '10px' }}></i></div>
                    <div className="d-flex align-items-center justify-content-center bg-white border rounded shadow-sm" onClick={collapseAll} title="전체 접기" style={{ width: '20px', height: '20px', cursor: 'pointer' }}><i className="fas fa-minus" style={{ fontSize: '10px' }}></i></div>
                  </div>
                </div>
                <div className="px-2 text-center border-start border-end border-secondary border-opacity-10 fw-bolder text-secondary" style={{ width: '151px', fontSize: '12px' }}>진행 기간</div>
                <div className="px-2 text-center fw-bolder text-secondary" style={{ width: '63px', fontSize: '12px' }}>진척도</div>
              </div>
            </div>

            {/* 우측 달력 헤더 */}
            <div className="flex-grow-1 position-relative d-flex" style={{ minWidth: 0 }}>
              <div className="flex-grow-1 overflow-hidden" ref={headerRightRef} style={{ minWidth: 0, overflowX: 'hidden' }}>
                <div className="d-flex">
                  <div style={{ width: canvasWidth + 'px', flexShrink: 0 }}>
                    {/* 월 렌더링 */}
                    <div className="d-flex bg-light border-bottom border-secondary border-opacity-10" style={{ height: '26px' }}>
                      {months.map((m, idx) => (
                        <div key={idx} className="d-flex align-items-center justify-content-center fw-bolder text-secondary border-end border-secondary border-opacity-25" style={{ width: (m.days * DAY_WIDTH) + 'px', flexShrink: 0, boxSizing: 'border-box', fontSize: '12px', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                          {m.label}
                        </div>
                      ))}
                    </div>
                    {/* 일 렌더링 */}
                    <div className="d-flex bg-white" style={{ height: '34px' }}>
                      {daysArray.map((day, idx) => {
                        const isWeekend = day.getDay() === 0 || day.getDay() === 6;
                        const isBoundary = idx === daysArray.length - 1 || (idx < daysArray.length - 1 && daysArray[idx + 1].getDate() === 1);
                        const isToday = isTodayInRange && day.getTime() === (new Date(new Date().setHours(0, 0, 0, 0))).getTime();

                        return (
                          <div key={idx} className={'d-flex flex-column align-items-center justify-content-center border-end border-secondary ' + (isBoundary ? 'border-opacity-50' : 'border-opacity-10')}
                            style={{ width: DAY_WIDTH + 'px', flexShrink: 0, backgroundColor: isWeekend ? '#f8f9fa' : '#fff', boxSizing: 'border-box' }}>
                            {isToday ? (
                              <span className="badge rounded-circle text-white d-flex align-items-center justify-content-center p-0 shadow-sm" style={{ backgroundColor: '#fd7e14', width: '22px', height: '22px', fontSize: '11px', transform: 'translateY(-1px)' }}>
                                {String(day.getDate()).padStart(2, '0')}
                              </span>
                            ) : (
                              <span className="fw-medium" style={{ fontSize: '11px', color: day.getDay() === 0 ? '#dc3545' : day.getDay() === 6 ? '#0d6efd' : '#868e96' }}>
                                {String(day.getDate()).padStart(2, '0')}
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  {/* 코너 블록 */}
                  <div className="d-flex flex-column bg-light" style={{ width: '8px', flexShrink: 0 }}>
                    <div className="border-bottom border-secondary border-opacity-10" style={{ height: '26px' }}></div><div style={{ height: '34px' }}></div>
                  </div>
                </div>
              </div>

              {/* 가로 플로팅 스크롤 버튼 (좌/우 화살표) - 달력 헤더 양끝에 항상 고정 */}
              {((tasksData.length > 0 && !isScrollLeftEnd) || hasMorePast) && (
                <div
                  className={`position-absolute d-flex align-items-center justify-content-center cursor-pointer transition-all ${isScrollLeftEnd && hasMorePast ? 'pulse-extend-btn shadow' : 'shadow-sm hover-shadow'}`}
                  onClick={scrollLeftAction}
                  style={{
                    left: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    backgroundColor: isScrollLeftEnd && hasMorePast ? '#0d6efd' : 'rgba(255, 255, 255, 0.9)',
                    border: isScrollLeftEnd && hasMorePast ? '2px solid #fff' : '1px solid rgba(13, 110, 253, 0.15)',
                    zIndex: 100,
                    backdropFilter: 'blur(4px)'
                  }}
                  title={isScrollLeftEnd && hasMorePast ? "과거(이전 달) 데이터가 있습니다. 클릭 시 캘린더를 1달 확장합니다." : "이전으로"}
                >
                  <i className={`fas fa-chevron-left ${isScrollLeftEnd && hasMorePast ? 'text-white' : 'text-primary opacity-50'} fs-6`}></i>
                </div>
              )}
              {((tasksData.length > 0 && !isScrollRightEnd) || hasMoreFuture) && (
                <div
                  className={`position-absolute d-flex align-items-center justify-content-center cursor-pointer transition-all ${isScrollRightEnd && hasMoreFuture ? 'pulse-extend-btn shadow' : 'shadow-sm hover-shadow'}`}
                  onClick={scrollRightAction}
                  style={{
                    right: '15px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    backgroundColor: isScrollRightEnd && hasMoreFuture ? '#0d6efd' : 'rgba(255, 255, 255, 0.9)',
                    border: isScrollRightEnd && hasMoreFuture ? '2px solid #fff' : '1px solid rgba(13, 110, 253, 0.15)',
                    zIndex: 100,
                    backdropFilter: 'blur(4px)'
                  }}
                  title={isScrollRightEnd && hasMoreFuture ? "미래(다음 달) 데이터가 있습니다. 클릭 시 캘린더를 1달 확장합니다." : "다음으로"}
                >
                  <i className={`fas fa-chevron-right ${isScrollRightEnd && hasMoreFuture ? 'text-white' : 'text-primary opacity-50'} fs-6`}></i>
                </div>
              )}
            </div>
          </div>

          {/* 하단 바디 래퍼 (실제 스크롤 영역) */}
          <div className="flex-grow-1 d-flex position-relative" style={{ minHeight: 0 }}>

            {/* 좌측 바디 (트리) */}
            <div className={`position-relative overflow-x-hidden hide-scrollbar border-end border-secondary border-opacity-25 ${inlineConfirmData ? 'overflow-hidden' : 'overflow-y-auto'}`} ref={bodyLeftRef} onScroll={handleBodyLeftScroll} style={{ width: listWidth + 'px', flexShrink: 0 }}>
              <div className="pb-2">
                {tasksData.map((project, index) => (
                  <React.Fragment key={project.id}>
                    <div className={'d-flex align-items-center bg-light bg-opacity-50 border-bottom ' + (index !== 0 ? 'border-top' : '')} onClick={() => toggleProject(project.id)} style={{ width: Math.max(420, listWidth) + 'px', height: ROW_HEIGHT + 'px', boxSizing: 'border-box', cursor: 'pointer' }}>
                      <div style={{ width: 'calc(100% - 214px)', position: 'relative' }}>
                        <div className="ps-2 fw-bold text-dark text-truncate d-flex align-items-center gap-2" style={{ fontSize: '13px', width: '100%' }}>
                          <i className={'fas fa-chevron-' + (expandedProjects[project.id] ? 'down' : 'right') + ' text-secondary opacity-75 text-center'} style={{ width: '14px', flexShrink: 0, fontSize: '11px' }}></i>
                          {filterCategory === 'received' && (
                            <i className="fas fa-user-tie text-primary" style={{ fontSize: '12px', flexShrink: 0 }} title="요청자"></i>
                          )}
                          {filterCategory !== 'received' && viewMode === 'byUser' && (
                            <i className="fas fa-user text-secondary" style={{ fontSize: '12px', flexShrink: 0 }} title="담당자"></i>
                          )}
                          <span className="text-truncate" onMouseEnter={(e) => handleMouseEnter(e, project.name)} onMouseLeave={handleMouseLeave} style={{ flex: 1, minWidth: 0 }}>
                            {project.name}
                          </span>
                        </div>
                      </div>
                      <div className="text-center fw-normal" style={{ width: '151px', fontSize: '11px', color: '#000000' }}>
                        {project.start} ~ {project.end}
                      </div>
                      <div className="text-center" style={{ width: '63px' }}>
                        <span className="badge border bg-secondary bg-opacity-10 text-secondary border-secondary border-opacity-50 shadow-sm">{project.progress}%</span>
                      </div>
                    </div>

                    {expandedProjects[project.id] && project.tasks.map(task => {
                      const todayDate = new Date(); todayDate.setHours(0, 0, 0, 0);
                      const isDelayed = todayDate > new Date(task.end) && task.progress < 100;
                      return (
                        <div key={task.id} className="d-flex align-items-center bg-white border-bottom border-secondary border-opacity-10 task-row" style={{ width: Math.max(420, listWidth) + 'px', height: ROW_HEIGHT + 'px', boxSizing: 'border-box' }}>
                          <div style={{ width: 'calc(100% - 214px)', position: 'relative' }}>
                            {(() => {
                              const isCc = filterCategory === 'cc' || filterCategory === 'referred';
                              const authorShortName = task.authorName ? task.authorName.split(' ')[0] : (task.author ? task.author.split(' ')[0] : '요청자');
                              return (
                                <div className={`${isCc ? 'ps-2' : 'ps-4'} pe-2 text-truncate d-flex align-items-center gap-1`} style={{ fontSize: '12px', color: '#495057', width: '100%' }}>
                                  {isCc ? (
                                    <span
                                      className="badge bg-light text-secondary border px-1 py-0 shadow-none flex-shrink-0"
                                      style={{ fontSize: '10px', fontWeight: '600', letterSpacing: '-0.3px', lineHeight: '16px' }}
                                      title={`요청자: ${task.author || task.authorName || '미지정'}`}
                                    >
                                      요청: {authorShortName}
                                    </span>
                                  ) : (
                                    <i className="fas fa-level-up-alt text-secondary opacity-25" style={{ transform: 'rotate(90deg)', flexShrink: 0 }}></i>
                                  )}
                                  {filterCategory !== 'received' && viewMode === 'byTask' && (
                                    <i className="fas fa-user text-secondary opacity-75 me-1" style={{ fontSize: '11px', flexShrink: 0 }} title="담당자"></i>
                                  )}
                                  <span
                                    className="text-truncate task-name-hover"
                                    onMouseEnter={(e) => handleMouseEnter(e, task.name)}
                                    onMouseLeave={handleMouseLeave}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setTooltipData(null);
                                      const woId = task.workOrderId || (task.id && task.id.startsWith('wo_') ? parseInt(task.id.split('_')[1], 10) : null);
                                      if (woId) setDetailModalWorkOrderId(woId);
                                    }}
                                    style={{ flex: 1, minWidth: 0, transition: 'color 0.15s', cursor: 'pointer' }}
                                    title="업무협조 상세 조회"
                                  >
                                    {task.name}
                                  </span>
                                </div>
                              );
                            })()}
                          </div>
                          <div
                            className="text-center text-secondary"
                            onClick={(e) => {
                              e.stopPropagation();
                              scrollToTaskCenter(task.start, task.end);
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.color = '#0d6efd'; e.currentTarget.style.textDecoration = 'underline'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.color = ''; e.currentTarget.style.textDecoration = 'none'; }}
                            style={{ width: '151px', fontSize: '11px', cursor: 'pointer', transition: 'color 0.15s' }}
                            title="클릭 시 해당 기간 타임라인 중앙으로 이동"
                          >
                            {task.start} <span className="opacity-50 mx-1">~</span> {task.end}
                          </div>
                          <div className="text-center" style={{ width: '63px' }}>
                            <span className={'badge border shadow-sm ' + (isDelayed ? 'bg-danger bg-opacity-10 text-danger border-danger' : task.progress === 100 ? 'bg-success bg-opacity-10 text-success border-success' : task.progress > 0 ? 'bg-primary bg-opacity-10 text-primary border-primary' : 'bg-warning bg-opacity-10 text-warning border-warning border-opacity-50')}>
                              {task.progress}%
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </React.Fragment>
                ))}
                <div style={{ height: '8px', flexShrink: 0 }}></div>
              </div>
            </div>

            {/* 우측 달력 바디 */}
            <div className="flex-grow-1 position-relative d-flex flex-column" style={{ minWidth: 0 }}>
              <div className={`flex-grow-1 bg-white ${inlineConfirmData ? 'overflow-hidden' : 'overflow-auto custom-scrollbar'}`} onClick={() => setTooltipData(null)} ref={bodyRightRef} onScroll={handleBodyRightScroll} style={{ minWidth: 0 }}>
                <div style={{ width: canvasWidth + 'px', overflow: 'hidden' }}>
                  <div className="position-relative" style={{ width: canvasWidth + 'px' }}>

                    {/* 오늘 날짜 선 */}
                    {isTodayInRange && (
                      <div className="position-absolute pointer-events-none" style={{ left: (todayOffset + (DAY_WIDTH / 2)) + 'px', top: 0, bottom: 0, width: '0', borderLeft: '2px dashed rgba(253, 126, 20, 0.7)', zIndex: 100, transform: 'translateX(-1px)' }}></div>
                    )}

                    {/* 세로 격자 */}
                    <div className="position-absolute top-0 bottom-0 d-flex pointer-events-none" style={{ left: 0, right: 0, zIndex: 0 }}>
                      {daysArray.map((day, idx) => {
                        const isBoundary = idx === daysArray.length - 1 || (idx < daysArray.length - 1 && daysArray[idx + 1].getDate() === 1);
                        return (
                          <div key={idx} className={'border-end border-secondary ' + (isBoundary ? 'border-opacity-50' : 'border-opacity-10')} style={{ width: DAY_WIDTH + 'px', flexShrink: 0, backgroundColor: (day.getDay() === 0 || day.getDay() === 6) ? '#f8f9fa' : 'transparent', boxSizing: 'border-box' }}></div>
                        );
                      })}
                    </div>

                    {tasksData.map((project, index) => (
                      <React.Fragment key={project.id}>
                        {/* 부모 막대 */}
                        <div className={'position-relative d-flex align-items-center border-bottom bg-light bg-opacity-50 ' + (index !== 0 ? 'border-top' : '')} onClick={() => toggleProject(project.id)} style={{ height: ROW_HEIGHT + 'px', boxSizing: 'border-box', zIndex: 1, cursor: 'pointer' }}>
                          {(() => {
                            const pos = getPosition(project.start, project.end);
                            const canEditParent = (project.tasks?.some(t => t.canEditDate) || isAdminRole || isSuperAdmin);
                            return (
                              <div
                                className="position-absolute rounded-pill overflow-hidden shadow-sm d-flex align-items-center"
                                onClick={(e) => e.stopPropagation()}
                                onMouseDown={(e) => { if (canEditParent) handleParentMouseDown(e, project.id, 'move'); }}
                                title={canEditParent ? '드래그: 기간 이동 / 좌우 가장자리: 시작·마감일 변경' : undefined}
                                style={{
                                  left: pos.left + 'px',
                                  width: pos.width + 'px',
                                  height: '22px',
                                  backgroundColor: '#ccfbf1',
                                  border: '1px solid #99f6e4',
                                  cursor: (dragInfo.current?.projectId === project.id && dragInfo.current?.isParent && dragInfo.current?.type === 'move') ? 'grabbing' : (canEditParent ? 'grab' : 'default'),
                                  userSelect: 'none'
                                }}
                              >
                                <div
                                  className="h-100 rounded-pill"
                                  style={{
                                    width: (project.progress || 0) + '%',
                                    backgroundColor: '#0d9488',
                                    transition: 'width 0.2s',
                                    pointerEvents: 'none'
                                  }}
                                />
                                <span
                                  className="position-absolute w-100 text-center fw-bold pointer-events-none"
                                  style={{
                                    fontSize: '11px',
                                    color: (project.progress || 0) > 50 ? '#ffffff' : '#0f766e',
                                    textShadow: (project.progress || 0) > 50 ? '0 1px 2px rgba(0,0,0,0.3)' : 'none'
                                  }}
                                >
                                  {project.progress || 0}%
                                </span>

                                {/* 부모 막대 좌우 리사이즈 핸들 (지시자/관리자 전용) */}
                                {canEditParent && (
                                  <div
                                    className="position-absolute top-0 bottom-0 d-flex align-items-center justify-content-start"
                                    style={{ left: 0, width: '12px', cursor: 'w-resize', zIndex: 11 }}
                                    onMouseDown={(e) => handleParentMouseDown(e, project.id, 'resize-left')}
                                    onClick={(e) => e.stopPropagation()}
                                    title="시작일 변경"
                                  />
                                )}
                                {canEditParent && (
                                  <div
                                    className="position-absolute top-0 bottom-0 d-flex align-items-center justify-content-end"
                                    style={{ right: 0, width: '12px', cursor: 'e-resize', zIndex: 11 }}
                                    onMouseDown={(e) => handleParentMouseDown(e, project.id, 'resize-right')}
                                    onClick={(e) => e.stopPropagation()}
                                    title="마감일 변경"
                                  />
                                )}
                              </div>
                            );
                          })()}
                        </div>

                        {/* 자식 막대 (캡슐) */}
                        {expandedProjects[project.id] && project.tasks.map(task => {
                          const pos = getPosition(task.start, task.end);
                          const colors = getCapsuleColor(task.status);
                          const statusBadge = getStatusBadge(task.status);

                          // 상태 텍스트([상태]) + 지시명 전체 길이 기반으로 막대 내부 공간 여유 판정
                          const totalTextLen = (task.status ? task.status.length + 2 : 0) + (task.name ? task.name.length : 0);
                          const reqSpace = totalTextLen * 11 + 24;
                          const availRight = Math.max(0, canvasWidth - (pos.left + pos.width) - 16);
                          const availLeft = Math.max(0, pos.left - 16);

                          // 막대 너비(pos.width)가 전체 텍스트보다 넉넉하게 넓을 때만 막대 안에 쏙 들어감
                          const isTextInside = pos.width >= reqSpace;
                          const goLeft = !isTextInside && (availRight < reqSpace) && (availLeft > availRight);
                          const maxOuterW = goLeft ? availLeft : availRight;

                          const todayDate = new Date(); todayDate.setHours(0, 0, 0, 0);
                          const isDelayed = new Date(task.end) < todayDate && task.progress < 100;
                          const capsuleBgColor = isDelayed ? '#ffe3e3' : colors.bg;
                          const capsuleFillColor = isDelayed ? '#fa5252' : colors.fill;

                          return (
                            <div key={task.id} className="task-row position-relative d-flex align-items-center border-bottom border-secondary border-opacity-10 hover-bg-light transition-all" style={{ height: ROW_HEIGHT + 'px', boxSizing: 'border-box', zIndex: 1 }}>

                              <div className="left-arrow position-absolute align-items-center justify-content-center rounded-circle bg-white shadow border border-primary" style={{ width: '20px', height: '20px', left: 0, zIndex: 10, display: 'none', cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); handleJumpToTask(task.start, task.end, 'start'); }} title="항목 시작 위치로 달력 연장 및 이동">
                                <i className="fas fa-caret-left text-primary" style={{ fontSize: '14px' }}></i>
                              </div>
                              <div className="right-arrow position-absolute align-items-center justify-content-center rounded-circle bg-white shadow border border-primary" style={{ width: '20px', height: '20px', left: 0, zIndex: 10, display: 'none', cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); handleJumpToTask(task.start, task.end, 'end'); }} title="항목 종료 위치로 달력 연장 및 이동">
                                <i className="fas fa-caret-right text-primary" style={{ fontSize: '14px' }}></i>
                              </div>

                              <div className={'task-capsule position-absolute rounded-pill shadow-sm d-flex align-items-center ' + (isDelayed ? 'border border-danger border-opacity-50' : '')}
                                data-left={pos.left} data-width={pos.width}
                                style={{ left: pos.left + 'px', width: pos.width + 'px', height: '22px', backgroundColor: capsuleBgColor, padding: '0 8px', cursor: task.isPureWatcher ? 'default' : 'pointer' }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (dragInfo.current.hasChanged || inlineConfirmData) return;
                                  clearTimeout(clickTimeout.current);
                                  const rect = e.currentTarget.getBoundingClientRect();
                                  clickTimeout.current = setTimeout(() => {
                                    let left = rect.right + 10; let top = rect.top + rect.height / 2 - 60;
                                    if (left + 240 > window.innerWidth) left = rect.left - 240 - 10;
                                    if (top < 150) { top = rect.bottom + 10; left = rect.left; }
                                    else if (top + 120 > window.innerHeight) { top = rect.top - 120 - 10; left = rect.left; }
                                    setTooltipData(prev => (prev && prev.taskId === task.id) ? null : { taskId: task.id, left, top, task, isDelayed });
                                  }, 200);
                                }}>

                                {isTextInside && (
                                  <div className="position-absolute w-100 h-100 d-flex align-items-center justify-content-center text-dark fw-bold px-2" style={{ left: 0, top: 0, pointerEvents: 'none', fontSize: '11px', zIndex: 1, overflow: 'hidden', whiteSpace: 'nowrap' }}>
                                    <span title={statusBadge.tooltip} style={{ color: statusBadge.color, marginRight: '4px', fontWeight: '800', pointerEvents: 'auto' }}>[{task.status}]</span>
                                    <span style={{ opacity: 0.85, overflow: 'hidden', textOverflow: 'ellipsis' }}>{task.name}</span>
                                  </div>
                                )}

                                <div
                                  className="h-100 rounded-pill position-relative overflow-hidden"
                                  style={{
                                    width: task.progress + '%',
                                    backgroundColor: capsuleFillColor,
                                    minWidth: task.progress > 0 ? '6px' : '0',
                                    zIndex: 2
                                  }}
                                >
                                  {isTextInside && (
                                    <div className="position-absolute h-100 d-flex align-items-center justify-content-center text-white fw-bold px-2" style={{ left: 0, top: 0, width: (pos.width - 4) + 'px', pointerEvents: 'none', fontSize: '11px', textShadow: '0px 1px 2px rgba(0,0,0,0.5)', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                                      <span title={statusBadge.tooltip} style={{ color: '#fff', marginRight: '4px', fontWeight: '800', pointerEvents: 'auto' }}>[{task.status}]</span>
                                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{task.name}</span>
                                    </div>
                                  )}
                                  {task.canEditProgress && (
                                    <div className="position-absolute top-0 bottom-0 d-flex align-items-center justify-content-center" style={{ right: 0, width: '14px', cursor: 'col-resize', zIndex: 10, backgroundColor: 'rgba(255,255,255,0.2)', borderLeft: '1px solid rgba(255,255,255,0.4)' }} onMouseDown={(e) => handleTaskMouseDown(e, project.id, task.id, 'progress')} onClick={e => e.stopPropagation()} title="진척도 조절">
                                      <div style={{ display: 'flex', gap: '2px' }}><div style={{ width: '1px', height: '8px', backgroundColor: 'rgba(255,255,255,0.8)' }}></div><div style={{ width: '1px', height: '8px', backgroundColor: 'rgba(255,255,255,0.8)' }}></div></div>
                                    </div>
                                  )}
                                </div>
                              </div>

                              {!isTextInside && (
                                goLeft ? (
                                  <div className="position-absolute fw-medium text-end d-flex align-items-center justify-content-end gap-1" style={{ right: (canvasWidth - pos.left + 8) + 'px', pointerEvents: 'none', zIndex: 5, whiteSpace: 'nowrap', fontSize: '11px', maxWidth: maxOuterW + 'px', overflow: 'hidden' }}>
                                    <span title={statusBadge.tooltip} style={{ color: statusBadge.color, fontWeight: '800', pointerEvents: 'auto' }}>[{task.status}]</span>
                                    <span className="text-secondary text-truncate">{task.name}</span>
                                  </div>
                                ) : (
                                  <div className="position-absolute fw-medium d-flex align-items-center gap-1" style={{ left: (pos.left + pos.width + 8) + 'px', pointerEvents: 'none', zIndex: 5, whiteSpace: 'nowrap', fontSize: '11px', maxWidth: maxOuterW + 'px', overflow: 'hidden' }}>
                                    <span title={statusBadge.tooltip} style={{ color: statusBadge.color, fontWeight: '800', pointerEvents: 'auto' }}>[{task.status}]</span>
                                    <span className="text-secondary text-truncate">{task.name}</span>
                                  </div>
                                )
                              )}
                            </div>
                          );
                        })}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 오늘 버튼 및 포탈 모달 렌더링 영역 */}
        {isTodayInRange && showTodayButton && (
          <button className="btn btn-sm btn-white border-secondary border-opacity-25 shadow rounded-pill px-3 py-2 d-flex align-items-center gap-2 transition-all" onClick={goToToday} style={{ position: 'absolute', right: '30px', bottom: '30px', zIndex: 900, color: '#fd7e14', fontWeight: 'bold', backgroundColor: '#fff' }}>
            <i className="far fa-calendar-check fs-6"></i> 오늘
          </button>
        )}

        {popupData && (
          <div className="bg-white border border-secondary border-opacity-25 shadow rounded px-2" style={{ position: 'fixed', top: (popupData.top + (popupData.height / 2)) + 'px', left: (popupData.left + 8) + 'px', transform: 'translateY(-50%)', zIndex: 9999, whiteSpace: 'nowrap', fontSize: '13px', pointerEvents: 'none', display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 8px' }}>
            <span className="fw-bolder" style={{ color: '#845ef7' }}>{popupData.text}</span>
          </div>
        )}

        {tooltipData && createPortal(
          <div className="position-fixed bg-dark text-white rounded-3 shadow-lg px-3 py-3" style={{ left: tooltipData.left + 'px', top: tooltipData.top + 'px', width: '240px', zIndex: 1040 }}>
            <div className="d-flex justify-content-between align-items-start mb-2">
              <div className="fw-bolder text-info fs-7 text-truncate pe-2">
                {(() => {
                  const badge = getStatusBadge(tooltipData.task.status);
                  return (
                    <span
                      className="badge me-2"
                      style={{ backgroundColor: badge.bg, color: badge.color, cursor: 'help', fontWeight: 'bold' }}
                      title={badge.tooltip}
                    >
                      {tooltipData.task.status || '대기'}
                    </span>
                  );
                })()}
                <span
                  style={{ cursor: 'pointer' }}
                  onMouseEnter={(e) => {
                    const parent = e.currentTarget.parentElement;
                    if (parent && parent.scrollWidth > parent.clientWidth) {
                      e.currentTarget.title = tooltipData.task.name;
                    } else {
                      e.currentTarget.removeAttribute('title');
                    }
                  }}
                  onClick={() => {
                    const task = tooltipData.task;
                    const woId = task.workOrderId || (typeof task.id === 'string' && task.id.startsWith('wo_') ? parseInt(task.id.split('_')[1], 10) : task.id);
                    if (woId) setDetailModalWorkOrderId(woId);
                    setTooltipData(null);
                  }}
                >
                  {tooltipData.task.name}
                </span>
              </div>
              <i className="fas fa-times text-secondary" style={{ cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); setTooltipData(null); }}></i>
            </div>
            <div className="opacity-75 mb-1 fs-8"><i className="far fa-calendar-alt me-2"></i>{tooltipData.task.start} ~ {tooltipData.task.end}</div>
            <div className="opacity-75 mb-1 fs-8"><i className="fas fa-tasks me-2"></i>진척도: <span className="text-white fw-bold">{tooltipData.task.progress}%</span></div>
            <div className="opacity-75 fs-8"><i className="fas fa-user-circle me-2"></i>담당자 매핑 완료</div>
          </div>,
          document.body
        )}
      </div>

      <InlineConfirmPopup data={inlineConfirmData} onConfirm={handleInlineConfirmAction} onCancel={handleInlineCancelAction} />

      {detailModalWorkOrderId && (
        <WorkOrderDetailModal
          open={Boolean(detailModalWorkOrderId)}
          workOrderId={detailModalWorkOrderId}
          onClose={() => setDetailModalWorkOrderId(null)}
          onRefreshList={() => {
            setDetailModalWorkOrderId(null);
            loadTimelineTasks();
          }}
        />
      )}
    </div>
  );
}
